// src/supplyLots.js
// ─────────────────────────────────────────────────────────────
// Point-in-time cost per letter (10/11/26) — pure: no React, no network.
// The screen is src/views/CostPerLetter.jsx (basis "Point in time", the
// default) + src/views/CostPerLetterLots.jsx; the letter exports are read by
// src/letterExports.js; the settings live in src/cplConfig.js.
//
// Reece, 10/10/26: "identify specific time expenses and what the cost per
// letter was when you include all of that, so it should absolutely not be
// just divided. It should be accounted for at that point in time … $30 for
// 50 envelopes … $80 for 1,500 envelopes." And: "when I was using the
// previous printer, I was spending a fortune on toner. Now … 30 to 50 times
// cheaper to operate."
//
//   LETTERS (letterLedger) — one per household (export) / lead (log) per
//     LOCAL day. Inside an envelope-project export's window the exports ARE
//     the letters ("there should not be any more mail … beyond what's listed
//     there, aside from maybe less than 100 handwritten letters"): the
//     export pieces + the Handwritten Letters tracker's letters (New Call);
//     every other mailing logged in the window was NOT mailed. Outside every
//     window the CRM's log counts. Nothing after the mailed-through date
//     counts (waiting). Stamps are the ceiling: a letter no stamp bought by
//     then (3 days' grace) could mail is "not backed" — never counted.
//   LOTS — every purchase PART of envelopes / paper / cardstock / cards /
//     letter stamps is its own FIFO layer at its own unit cost (same day =
//     receipt order). Each letter takes the oldest stock on the shelf that
//     day. A part with no count gets one: the receipt → Reece's rules
//     (Staples résumé / fine-linen envelopes = 50 a box; a ream = 500) → a
//     box size by price → the median counted price → a time window.
//   WINDOWS — things with no unit: ink & toner (each printer era's ink over
//     that era's printed letters, or each purchase until the next),
//     printers (a printer's era ends when the next printer is bought — a
//     retired printer over its letters; the current one straight-line over
//     its life), business cards (10/11/26 — "they didn't all get a card":
//     each order over the letters mailed until the next order, the last
//     until the cards-through date; a dated change weighs its letters — 0 =
//     no card), data (each purchase until the site's
//     next, 30–60 days), add-on stamps, the mailhouse (its invoices where
//     its letters went out).
//   EXACT — integer cents; a FIFO part take divRound(leftCents × n, left),
//     a lot's last unit its last cent; windows allocate (largest
//     remainder). spent = charged + on hand + waiting + printer not yet used
//     up + adjustments, to the cent.
// ─────────────────────────────────────────────────────────────

import { expenseItemParts, expenseVendorGroup, dataSiteIn, NO_VENDOR, NO_DETAIL, POSTAGE_KINDS } from './expenseItems';
import { mailingEvents, lettersByPeriod, isMailhouseRow, isParcelPostage, mailhousePlacement, stampPrices, stampPriceOn, allocate, divRound, costPerLetter, diffInfo, periodsBetween, addDays, isDay, monthEnd, fmtMoneyCents, fmtInt, fmtDay, CARDS_UNTIL_DEFAULT } from './costPerLetter';
import { localDay } from './regression';

// Stack order, bottom → top (the chart / table / compare).
var PIT_KEYS=["Data","Paper","Cardstock","Envelopes","Ink & Toner","Printers","Postage","Business Cards"];
var PIT_LABEL={"Data":"Data","Paper":"Paper","Cardstock":"Cardstock","Envelopes":"Envelopes","Ink & Toner":"Ink & Toner","Printers":"Printers","Postage":"Postage","Business Cards":"Business cards"};
var LOT_COMPS=["Envelopes","Paper","Cardstock","Business Cards"];
var UNIT_OF={"Envelopes":"envelopes","Paper":"sheets","Cardstock":"sheets","Business Cards":"cards","Postage":"stamps"};
var UNIT_ONE={"envelopes":"envelope","sheets":"sheet","cards":"card","stamps":"stamp"};
// Box sizes a store line could be (box-by-price).
var BOX_SIZES={"Envelopes":[50,100,250,500],"Paper":[50,100,250,500],"Cardstock":[50,100,150,250],"Business Cards":[250,500,1000]};
// A printer itself (Equipment parts) — never ink, toner, paper, a cable…
var PRINTER_RE=/\b(printers?|imageclass|megatank|pixma|ecotank|laserjet|officejet|workforce|maxify)\b/i;
var NOT_PRINTER_RE=/\b(ink|toner|cartridges?|paper|cables?|stand|cover|labels?)\b/i;
var MS_DAY=864e5;
// How business cards are charged by default (10/11/26 — Reece: "they didn't all get a card").
var CARDS_WINDOW_NOTE="spread over the letters mailed while each order was in use — not every letter got one";
// Every setting (config.settings) with its default; every field optional in storage.
var DEFAULTS={v:1,
  mailedThrough:null,             // null = the latest export's asOf, else today
  graceDays:3,                    // a stamp receipt may trail the mailing by up to 3 days
  paperPerLetter:1,envelopesPerLetter:1,cardsPerLetter:1,
  cardstock:"fromPurchases",      // or a number per letter
  cardsUntil:CARDS_UNTIL_DEFAULT, // "" = every letter
  cardsMode:"window",             // "window" (each order over the letters mailed until the next) | "fifo" (one a letter while they last)
  usage:[],                       // dated overrides [{comp, from, to, perLetter}]
  inkMode:"era",                  // "era" | "restock"
  printerLifeMonths:24,printerJoinDays:7,
  dataMinDays:30,dataMaxDays:60,addOnMaxDays:60,refDays:60,usedLateDays:60,
  staplesPremiumEnvPerBox:50,reamSheets:500,
  notLetterCost:[],               // expense ids left out by hand ("Not a letter cost")
  usedFrom:{},                    // {expenseId: day} — its supply lots go on the shelf that day
  printerEras:{}};                // {expenseId: {retiredOn?, inUse? (kept printing after the next printer came), lifeMonths?, notPrinter?}}

// ── Small pieces ─────────────────────────────────────────────

function oneLine(s){return String(s==null?"":s).replace(/\s+/g," ").trim();}
function pad2(n){return(n<10?"0":"")+n;}
function dayNum(day){var m=/^(\d{4})-(\d{2})-(\d{2})$/.exec(day||"");return m?Date.UTC(+m[1],+m[2]-1,+m[3])/MS_DAY:NaN;}
function dayFromNum(n){var d=new Date(n*MS_DAY);return d.getUTCFullYear()+"-"+pad2(d.getUTCMonth()+1)+"-"+pad2(d.getUTCDate());}
function daysBetween(a,b){return dayNum(b)-dayNum(a);}
function todayLocal(){var d=new Date();return d.getFullYear()+"-"+pad2(d.getMonth()+1)+"-"+pad2(d.getDate());}
function median(a){var s=a.filter(function(x){return isFinite(x);}).slice().sort(function(x,y){return x-y;});var n=s.length;return n?(n%2?s[(n-1)/2]:(s[n/2-1]+s[n/2])/2):null;}
function sum(a,f){var t=0;(a||[]).forEach(function(x){t+=f?f(x):x;});return t;}
function cents(v){return Math.round((Number(v)||0)*100);}
function centsLabel(c){c=Number(c)||0;return c<100?(Math.round(c*10)/10).toFixed(1)+"¢":"$"+(c/100).toFixed(c<1000?3:2);}
function unitWord(comp,n){var u=UNIT_OF[comp]||"units";return n===1?(UNIT_ONE[u]||u):u;}
// Monday of the week a day is in (weeks run Mon–Sun).
function weekKey(day){var n=dayNum(day);if(isNaN(n))return null;var dow=(new Date(n*MS_DAY).getUTCDay()+6)%7;return dayFromNum(n-dow);}
function cmpLot(a,b){return a.day<b.day?-1:a.day>b.day?1:a.exId<b.exId?-1:a.exId>b.exId?1:(a.partIndex||0)-(b.partIndex||0);}
function looksImported(ex){return!!(ex&&(ex.imported||/(^|\n)\s*Imported from /.test(String(ex.notes||""))));}
// What a purchase was, from its description: "Amazon — Canon toner 055" →
// "Canon toner 055" (the vendor part before " — " off), clipped.
function whatOf(desc){var d=oneLine(desc);var i=d.indexOf(" — ");if(i>0)d=d.slice(i+3);return d.length>48?d.slice(0,47).replace(/\s+\S*$/,"")+"…":d;}

// The settings, normalised: numbers clamped, days checked, unknown keys dropped.
function num(v,def,min,max,int){if(v===""||v==null)return def;var n=Number(v);if(!isFinite(n))return def;if(int)n=Math.round(n);return Math.max(min,Math.min(max,n));}
function normalizeSettings(raw){
  raw=raw&&typeof raw==="object"?raw:{};
  var s={v:1};
  s.mailedThrough=isDay(raw.mailedThrough)?raw.mailedThrough:null;
  s.graceDays=num(raw.graceDays,DEFAULTS.graceDays,0,30,true);
  s.paperPerLetter=num(raw.paperPerLetter,1,0,20);
  s.envelopesPerLetter=num(raw.envelopesPerLetter,1,0,5);
  s.cardsPerLetter=num(raw.cardsPerLetter,1,0,10);
  s.cardstock=raw.cardstock==null||raw.cardstock==="fromPurchases"?"fromPurchases":num(raw.cardstock,"fromPurchases",0,10);
  s.cardsUntil=raw.cardsUntil===""||raw.cardsUntil===null?"":isDay(raw.cardsUntil)?raw.cardsUntil:DEFAULTS.cardsUntil;
  s.cardsMode=raw.cardsMode==="fifo"?"fifo":"window";
  s.usage=(Array.isArray(raw.usage)?raw.usage:[]).filter(function(u){return u&&LOT_COMPS.indexOf(u.comp)>=0&&isFinite(Number(u.perLetter));}).map(function(u){
    return{comp:u.comp,from:isDay(u.from)?u.from:"",to:isDay(u.to)?u.to:"",perLetter:num(u.perLetter,1,0,20)};}).slice(0,50);
  s.inkMode=raw.inkMode==="restock"?"restock":"era";
  s.printerLifeMonths=num(raw.printerLifeMonths,24,1,120,true);
  s.printerJoinDays=num(raw.printerJoinDays,7,0,60,true);
  s.dataMinDays=num(raw.dataMinDays,30,1,365,true);
  s.dataMaxDays=Math.max(s.dataMinDays,num(raw.dataMaxDays,60,1,730,true));
  s.addOnMaxDays=num(raw.addOnMaxDays,60,1,365,true);
  s.refDays=num(raw.refDays,60,1,3650,true);
  s.usedLateDays=num(raw.usedLateDays,60,1,3650,true);
  s.staplesPremiumEnvPerBox=num(raw.staplesPremiumEnvPerBox,50,1,5000,true);
  s.reamSheets=num(raw.reamSheets,500,1,10000,true);
  s.notLetterCost=(Array.isArray(raw.notLetterCost)?raw.notLetterCost:[]).filter(function(x){return typeof x==="string"&&x;}).slice(0,1000);
  s.usedFrom={};Object.keys(raw.usedFrom&&typeof raw.usedFrom==="object"?raw.usedFrom:{}).forEach(function(k){if(isDay(raw.usedFrom[k]))s.usedFrom[k]=raw.usedFrom[k];});
  s.printerEras={};Object.keys(raw.printerEras&&typeof raw.printerEras==="object"?raw.printerEras:{}).forEach(function(k){
    var e=raw.printerEras[k];if(!e||typeof e!=="object")return;var o={};
    if(isDay(e.retiredOn))o.retiredOn=e.retiredOn;
    if(e.inUse)o.inUse=true;
    if(e.lifeMonths!=null&&isFinite(Number(e.lifeMonths)))o.lifeMonths=num(e.lifeMonths,24,1,120,true);
    if(e.notPrinter)o.notPrinter=true;
    if(Object.keys(o).length)s.printerEras[k]=o;
  });
  return s;
}

var partsCache=new WeakMap();
function cachedParts(ex){var p=partsCache.get(ex);if(!p){p=expenseItemParts(ex);partsCache.set(ex,p);}return p;}

// ── Left out ─────────────────────────────────────────────────

// A positive row that is really a refund: its description says so ("Amazon —
// Refund: …", "Return of …") or every part of its Items line is negative.
var REFUND_DESC_RE=/(^|—\s*)(refund|advance refund|return(?![\s-]*(?:address|addr|envelopes?|envs?|env|labels?|lbls?|mail(?:ers?|ing)?|postage|stamps?|cards?|reply)\b))\b/i;
function looksLikeRefund(ex,partsOf){
  if(!ex||!((Number(ex.amount)||0)>0))return false;
  if(REFUND_DESC_RE.test(oneLine(ex.desc)))return true;
  var line=String(ex.notes||"").split("\n").filter(function(l){return/^\s*items?\s*:/i.test(l);})[0];
  if(!line)return false;
  var amts=line.replace(/^\s*items?\s*:\s*/i,"").match(/-?\s*\$\s*-?[\d,]*\.?\d+/g)||[];
  return amts.length>0&&amts.every(function(a){return/-/.test(a);});
}
// (Never a purchase OF return things: "Return Envelopes #9, 500 count",
// "Return address labels", "Return-address envelopes" are envelopes and
// labels he bought.)
// Possible duplicates (display only — "keep those in"): a hand row and an
// imported row of the same item type ≤ 3 days apart whose parts are within
// 10% of the larger. A hand row whose notes keep it ("Kept as a …") isn't.
function possibleDuplicates(expenses,partsOf){
  partsOf=partsOf||cachedParts;
  var out=new Map();
  var list=(expenses||[]).filter(function(e){return e&&localDay(e.date)&&(Number(e.amount)||0)>0;});
  var hand=list.filter(function(e){return!looksImported(e)&&!/(^|\n)\s*Kept as a/.test(String(e.notes||""));});
  var imp=list.filter(looksImported);
  hand.forEach(function(h){
    var hd=localDay(h.date);
    partsOf(h).forEach(function(hp){
      if(!hp.type||!hp.cents||!UNIT_OF[hp.type])return;
      imp.forEach(function(o){
        var od=localDay(o.date);if(Math.abs(daysBetween(hd,od))>3)return;
        partsOf(o).forEach(function(op){
          if(op.type!==hp.type||!op.cents)return;
          var big=Math.max(Math.abs(op.cents),Math.abs(hp.cents));
          if(Math.abs(op.cents-hp.cents)*10>big)return;
          var a=out.get(h.id);if(!a){a=[];out.set(h.id,a);}
          if(!a.some(function(x){return x.id===o.id;}))a.push({id:o.id,why:hp.type+" "+fmtMoneyCents(op.cents)+" on "+fmtDay(od)+" at "+(op.vendor||expenseVendorGroup(o)),cents:op.cents,day:od,vendor:op.vendor||expenseVendorGroup(o)});
        });
      });
    });
  });
  return out;
}

// Rows that could be letter supplies but carry no item type (review RD-5):
// every part "No item detail" / untyped, and a Marketing row or one whose
// words name a letter supply ("Amazon Materials" $150, "Additional Paper and
// Office Items" under Office/Equipment). They're in no figure and in no
// left-out list — shown under "How it adds up" (and beside a gap they may
// have filled) so a gap's purchase isn't added twice. A row typed as
// something else ("Office supplies") is left out on purpose and isn't here.
var LETTER_WORDS_RE=/\b(paper|envelopes?|stationery|stationary|stamps?|postage|toner|ink|cardstock|card stock|business cards?)\b/i;
function untypedRows(live,partsOf){
  var out=[];
  (live||[]).forEach(function(e){
    if(!((Number(e.amount)||0)>0))return;
    var ps=partsOf(e).filter(function(p){return p.cents;});
    if(!ps.length||ps.some(function(p){return p.type&&p.type!==NO_DETAIL;}))return;
    if(e.cat!=="Marketing"&&!LETTER_WORDS_RE.test(oneLine((e.vendor||"")+" "+(e.desc||""))))return;
    var day=localDay(e.date);if(!day)return;
    out.push({id:e.id,day:day,cents:sum(ps,function(p){return p.cents;}),vendor:ps[0].vendor||expenseVendorGroup(e),desc:oneLine(e.desc),cat:e.cat||"",itemsLine:/(^|\n)\s*items?\s*:/i.test(String(e.notes||""))});
  });
  return out.sort(function(a,b){return a.day<b.day?-1:a.day>b.day?1:a.id<b.id?-1:1;});
}
// Marketing rows that are no letter part (review RD-5, second pass): not one
// part is a letter item (Data, Paper, Cardstock, Envelopes, Ink & Toner,
// Postage, Business Cards) or a printer era's — typed Office Supplies
// ("Office supplies" $215, "Staples Office Suplplies" $647), Labels, Pens &
// Markers, the mailing machine, a non-printer Equipment part, or that mixed
// with untyped parts. untypedRows drops a row with any typed part, so these
// were in no figure and on no list. Listed under "How it adds up" as
// "Marketing, not a letter part", and beside a gap they're dated in or up to
// NEAR_GAP_DAYS before (the view). Never in spent — the reconcile is
// unchanged. skip: the ids untypedRows lists (never listed twice).
var LETTER_PART_TYPES={"Data":1,"Paper":1,"Cardstock":1,"Envelopes":1,"Ink & Toner":1,"Postage":1,"Business Cards":1};
var NEAR_GAP_DAYS=14;
function notLetterPartRows(live,partsOf,eras,skip){
  partsOf=partsOf||cachedParts;
  var printerIds=new Set();(eras||[]).forEach(function(e){(e.ids||[]).forEach(function(id){printerIds.add(id);});});
  var out=[];
  (live||[]).forEach(function(e){
    if(!e||e.cat!=="Marketing"||(skip&&skip.has(e.id))||printerIds.has(e.id))return;
    var ps=partsOf(e).filter(function(p){return p.cents;});
    if(!ps.length||ps.some(function(p){return LETTER_PART_TYPES[p.type];}))return;
    var day=localDay(e.date);if(!day)return;
    var types=[];ps.forEach(function(p){var t=p.type||"no item type";if(types.indexOf(t)<0)types.push(t);});
    out.push({id:e.id,day:day,cents:sum(ps,function(p){return p.cents;}),vendor:ps[0].vendor||expenseVendorGroup(e),desc:oneLine(e.desc),cat:e.cat,types:types,itemsLine:/(^|\n)\s*items?\s*:/i.test(String(e.notes||""))});
  });
  return out.sort(function(a,b){return a.day<b.day?-1:a.day>b.day?1:a.id<b.id?-1:1;});
}

// ── Letters: the ledger ─────────────────────────────────────

function emptyLetters(){return{printed:0,newCall:0,mailhouse:0,envOnly:0,notBacked:0,waiting:0,logged:0,hwMailer:0};}
// Letters per LOCAL day, by precedence (see the header). opts: {owner,
// loans, today, events, sources (config.letterSources), mailedThrough,
// partsOf}. → {days: Map(day → {printed, newCall, mailhouse, envOnly,
// waiting (of it waitingMh mailhouse letters, waitingEnv envelope-only
// pieces — what a shelf count after mailedThrough takes off the records),
// logged, hwMailer, source}), mailedThrough, windows, logNotCounted:
// Map(month → n), drops, byMonth: Map(month → {exports, log, logNotCounted}),
// events}.
function letterLedger(mktgLeads,expenses,opts){
  opts=opts||{};
  var today=opts.today||todayLocal();
  var ev=opts.events||mailingEvents(mktgLeads,{owner:opts.owner,loans:opts.loans,today:today});
  var L=lettersByPeriod(mktgLeads,expenses,{owner:opts.owner,loans:opts.loans,today:today,partsOf:opts.partsOf,events:ev});
  var sources=(opts.sources||[]).filter(function(s){return s&&s.window&&isDay(s.window.from)&&isDay(s.window.to)&&s.days;});
  var asOfMax=sources.reduce(function(m,s){return isDay(s.asOf)&&(!m||s.asOf>m)?s.asOf:m;},null);
  var mailedThrough=isDay(opts.mailedThrough)?opts.mailedThrough:asOfMax||today;
  // The import that owns a day: the latest-imported one whose window covers it.
  var ordered=sources.slice().sort(function(a,b){return String(b.importedAt||"").localeCompare(String(a.importedAt||""));});
  var ownerOf=function(day){for(var i=0;i<ordered.length;i++){var w=ordered[i].window;if(day>=w.from&&day<=w.to)return ordered[i];}return null;};
  var days=new Map();
  var get=function(d){var x=days.get(d);if(!x){x=Object.assign(emptyLetters(),{source:"",waitingMh:0,waitingEnv:0});days.set(d,x);}return x;};
  var logNotCounted=new Map();var byMonth=new Map();
  var mrow=function(k){var x=byMonth.get(k);if(!x){x={month:k,exports:0,log:0,logNotCounted:0};byMonth.set(k,x);}return x;};
  var addLog=function(day,kind,n){
    var m=day.slice(0,7);mrow(m).log+=n;
    var owner=ownerOf(day);
    var x=get(day);
    // Inside an export window only the Handwritten Letters tracker (New Call) counts.
    if(owner&&kind!=="newCall"){x.logged+=n;mrow(m).logNotCounted+=n;logNotCounted.set(m,(logNotCounted.get(m)||0)+n);return;}
    if(day>mailedThrough){x.waiting+=n;if(kind==="mailhouse")x.waitingMh+=n;return;}
    x[kind]+=n;if(!x.source)x.source=owner?"exports":"log";
  };
  ev.events.forEach(function(e){addLog(e.day,e.group==="letters"?"newCall":e.method==="Mailhouse"?"mailhouse":"printed",e.n);});
  (L.drops||[]).forEach(function(d){(d.addedDays||[]).forEach(function(x){addLog(x.day,d.mailhouse?"mailhouse":"printed",x.n);});});
  sources.forEach(function(s){
    Object.keys(s.days).forEach(function(day){
      var r=s.days[day]||{};var owner=ownerOf(day);
      // (we = the envelope-only pieces among w; an older record without it reads every w as a letter)
      if(r.w&&!owner){var xw=get(day);xw.waiting+=r.w;xw.waitingEnv+=Math.min(r.w,Math.max(0,r.we||0));}
      if(owner!==s)return;
      var p=Math.max(0,r.p||0),e=Math.max(0,r.e||0),hw=Math.max(0,r.hw||0);
      if(!p&&!e)return;
      mrow(day.slice(0,7)).exports+=p+e;
      var x=get(day);
      if(day>mailedThrough){x.waiting+=p+e;x.waitingEnv+=e;return;}
      x.printed+=p;x.envOnly+=e;x.hwMailer+=hw;x.source="exports";
    });
  });
  return{days:days,mailedThrough:mailedThrough,windows:ordered.map(function(s){return{id:s.id,from:s.window.from,to:s.window.to,asOf:s.asOf,importedAt:s.importedAt};}),
    logNotCounted:logNotCounted,drops:L.drops,byMonth:byMonth,events:ev,letters:L};
}

// ── Purchase parts ───────────────────────────────────────────

// Every live expense's parts → [{key, exId, partIndex, day (bought), shelf
// (on the shelf from — usedFrom), type, variety, brand, cents, qty, units,
// unitName, fromLine, vendor, desc, hand, mailhouse, parcelRow, family}].
function listParts(expenses,partsOf,settings){
  var out=[];
  (expenses||[]).forEach(function(ex){
    var day=localDay(ex.date);if(!day)return;
    var parts=partsOf(ex);
    var mh=null,parcel=null;
    parts.forEach(function(p,i){
      if(!p.cents)return;
      if(p.type==="Postage"&&mh===null){mh=isMailhouseRow(ex,partsOf);parcel=!mh&&isParcelPostage(ex);}
      out.push({key:ex.id+"#"+i,exId:ex.id,partIndex:i,ex:ex,day:day,shelf:(settings.usedFrom&&settings.usedFrom[ex.id])||day,type:p.type,variety:p.variety||null,kindGuess:!!p.kindGuess,brand:p.brand||null,
        cents:p.cents,qty:p.qty==null?null:p.qty,units:p.units==null?null:p.units,unitName:p.unitName||null,fromLine:!!p.fromLine,vendor:p.vendor||expenseVendorGroup(ex),
        desc:oneLine(ex.desc),hand:!looksImported(ex),mailhouse:p.type==="Postage"&&(!!mh||p.variety===POSTAGE_KINDS.MAILHOUSE),parcelRow:p.type==="Postage"&&!!parcel});
    });
  });
  return out;
}

// ── Refunds: net into the purchase they refund ───────────────

// lots (mutable: units?, cents, exId, bought, kind) + refunds (negative
// parts as records) → adjustments [{comp, day, cents, key, exId}]. The
// target: the tied purchase (refundTies: refund id → [target ids]) — its
// lot of the same kind first, then the same count + cents; untied: the
// newest earlier lot with the same count + cents, else the same cents, else
// the same kind, else the newest. Units and cents net into it; the rest
// spills to the next-newest earlier lots; cents beyond every lot are an
// adjustment on the refund's day. A lot left with cents but no units gives
// them back as an adjustment too (its money can't ride on a letter).
function netRefunds(lots,refunds,ties,comp){
  var adj=[];
  refunds.slice().sort(function(a,b){return a.day<b.day?-1:a.day>b.day?1:0;}).forEach(function(r){
    var c=Math.abs(r.cents),u=Math.abs(r.units||0);
    var counts=lots.some(function(l){return l.units!=null;});
    var earlier=lots.filter(function(l){return l.bought<=r.day&&(l.cents>0||(l.units||0)>0);}).sort(function(a,b){return cmpLot(b,a);});
    var tied=ties&&ties.get?ties.get(r.exId):null;
    var t=null;
    if(tied&&tied.length){
      var tl=lots.filter(function(l){return tied.indexOf(l.exId)>=0&&(l.cents>0||(l.units||0)>0);}).sort(function(a,b){return cmpLot(b,a);});
      t=tl.filter(function(l){return r.kind&&l.kind===r.kind;})[0]||tl.filter(function(l){return u&&l.units===u&&l.cents===c;})[0]||tl.filter(function(l){return l.cents===c;})[0]||tl[0]||null;
    }
    if(!t)t=earlier.filter(function(l){return u&&counts&&l.units===u&&l.cents===c;})[0]||earlier.filter(function(l){return l.cents===c;})[0]||earlier.filter(function(l){return r.kind&&l.kind===r.kind;})[0]||earlier[0]||null;
    var chain=t?[t].concat(earlier.filter(function(l){return l!==t;})):[];
    for(var i=0;i<chain.length&&(u||c);i++){
      var l=chain[i];
      var du=l.units!=null?Math.min(u,l.units):0,dc=Math.min(c,l.cents);
      if(l.units!=null)l.units-=du;l.cents-=dc;u-=du;c-=dc;
      l.refunded=(l.refunded||0)+dc;l.refundedUnits=(l.refundedUnits||0)+du;
      (l.refundIds=l.refundIds||[]).push(r.exId);
    }
    if(c)adj.push({comp:comp,day:r.day,cents:-c,key:r.key,exId:r.exId,why:"refund beyond the purchases it could come off"});
  });
  lots.forEach(function(l){
    if(l.units!=null&&l.units<=0&&l.cents>0){adj.push({comp:comp,day:l.bought,cents:l.cents,key:l.key,exId:l.exId,why:"what's left of a purchase whose count was refunded"});l.cents=0;}
  });
  return adj;
}

// ── Stamps ───────────────────────────────────────────────────

// Postage parts → {lots (letter stamps: units, cents, how), addOns (cents
// only — never letters), parcels (left out: not a letter cost), adjustments}.
// The mailhouse's postage is placed with its letters (mailhousePlacement).
function stampLots(parts,opts){
  opts=opts||{};
  var prices=opts.prices||stampPrices({});
  var lots=[],addOns=[],parcels=[],refunds=[];
  (parts||[]).forEach(function(p){
    if(p.type!=="Postage"||!p.cents||p.mailhouse||p.variety===POSTAGE_KINDS.MAILHOUSE)return;
    var counted=(p.units||0)>0&&p.unitName==="stamps";
    var base={key:p.key,exId:p.exId,partIndex:p.partIndex,comp:"Postage",day:p.day,bought:p.day,vendor:p.vendor,kind:p.variety,cents:p.cents,hand:p.hand,desc:p.desc};
    // A part TYPED Parcel / Add-on (its Items-line bracket) is never letter
    // stamps, whatever count it carries. A kind only guessed from a hand
    // row's description (kindGuess), or a parcel row with no kind, yields
    // to a letter-stamp count on its Items line — what the stamp check's
    // "Letter stamps it bought" box writes; the Add form's "How many?" and a
    // count in the description never make one for add-on / parcel postage.
    var letterCount=counted&&(!p.variety||p.kindGuess);
    if((p.variety===POSTAGE_KINDS.PARCEL||(!p.variety&&p.parcelRow))&&!letterCount){parcels.push(base);return;}
    if(p.variety===POSTAGE_KINDS.ADDON&&!letterCount){addOns.push(base);return;}
    var balance=p.variety===POSTAGE_KINDS.BALANCE||p.vendor==="Stamps.com";
    var price=balance?prices.stampsCom:stampPriceOn(p.day,p.vendor,prices);
    var units=counted?p.units:Math.floor(Math.abs(p.cents)/price);
    var rec=Object.assign(base,{units:units,how:counted?"receipt":balance?"balance":"price",
      why:counted?"the receipt's letter-stamp count":balance?"Stamps.com balance ÷ "+price+"¢ (at most)":"$ ÷ "+price+"¢ — the letter rate on "+fmtDay(p.day)});
    if(p.cents<0){rec.units=units;refunds.push(rec);}else lots.push(rec);
  });
  lots.sort(cmpLot);
  var adjustments=netRefunds(lots,refunds,opts.refundTies,"Postage");
  return{lots:lots.filter(function(l){return l.units>0;}),addOns:addOns,parcels:parcels,adjustments:adjustments,prices:prices,
    zero:lots.filter(function(l){return!(l.units>0);})};
}

// ── FIFO ─────────────────────────────────────────────────────

// lots [{key, day, units, cents}] (oldest first) + demand [{day, units}]
// (by day) → {out:[{day, need, served, short, cents, takes:[{key, units,
// cents}]}], lots (each + left, leftC, used [{day, units, cents, waste?}]),
// onHand {units, cents}}. A lot serves days ≥ its day − lead (a demand row
// with noLead: only lots on the shelf that day). A part take costs
// divRound(leftC × n, left); a lot's last unit takes its last cent.
function runFifo(lots,demand,opts){
  var lead=(opts&&opts.lead)||0;
  var q=(lots||[]).filter(function(l){return l.units>0;}).slice().sort(cmpLot).map(function(l){return Object.assign({},l,{left:l.units,leftC:l.cents,used:[],src:l});});
  var out=[];var start=0;
  (demand||[]).forEach(function(dm){
    var need=dm.units;var c=0;var takes=[];
    var lim=lead&&!dm.noLead?addDays(dm.day,lead):dm.day;
    while(start<q.length&&q[start].left<=0)start++;
    for(var k=start;k<q.length&&need>0;k++){
      var l=q[k];if(l.day>lim)break;if(l.left<=0)continue;
      var n=Math.min(need,l.left);var cc=n===l.left?l.leftC:divRound(l.leftC*n,l.left);
      l.left-=n;l.leftC-=cc;need-=n;c+=cc;takes.push({key:l.key,units:n,cents:cc});
      var u={day:dm.day,units:n,cents:cc};if(dm.waste)u.waste=true;l.used.push(u);
    }
    out.push({day:dm.day,need:dm.units,served:dm.units-need,short:need,cents:c,takes:takes});
  });
  var onHand={units:0,cents:0};q.forEach(function(l){onHand.units+=l.left;onHand.cents+=l.leftC;});
  return{out:out,lots:q,onHand:onHand};
}

// Letter-stamp shelf counts (review RD-1 — the Letter stamps tile's "Count
// your shelf" used to be saved and ignored). The supplies' rule: the records
// at a count's day (the stamps on the shelf then, after the counted letters up
// to it — less what the WAITING letters mailed by then used) against the
// count. Fewer on the shelf → the difference went without a letter (misprints,
// pulled duplicates): a {waste} row takes it off the shelf that day (no grace
// days — only what was on the shelf), charged later to the stamped letters
// since the count before. More → found stamps at the letter rate that day
// (estimated — on no receipt), on the shelf from the count's day.
// → {lots, demand (the letters + waste rows; letters first on a day), notes}
function stampShelf(lots,demand,waiting,counts,settings,thru,prices){
  var cs=(counts||[]).filter(function(s){return s&&s.comp==="Postage"&&isDay(s.day)&&isFinite(Number(s.units))&&Number(s.units)>=0;}).sort(function(a,b){return a.day<b.day?-1:1;});
  var out={lots:(lots||[]).slice(),demand:(demand||[]).slice(),notes:[]};
  if(!cs.length)return out;
  var waitDem=(waiting||[]).map(function(x){return{day:x.day,units:x.printed+x.newCall+x.envOnly};}).filter(function(x){return x.units>0;});
  var order=function(a,b){return a.day<b.day?-1:a.day>b.day?1:(a.waste?1:0)-(b.waste?1:0);};
  cs.forEach(function(sc,ci){
    var R0=runFifo(out.lots.filter(function(l){return l.day<=sc.day;}),out.demand.filter(function(x){return x.day<=sc.day;}),{lead:settings.graceDays}).onHand.units;
    var wu=Math.min(R0,sum(waitDem.filter(function(x){return x.day<=sc.day;}),function(x){return x.units;}));
    var R=R0-wu,C=Math.round(Number(sc.units));
    var by=wu?" (after the "+fmtInt(wu)+" "+(wu===1?"stamp":"stamps")+" the letters mailed after "+fmtDay(thru)+" used)":"";
    if(C<R){
      out.demand.push({day:sc.day,units:R-C,letters:0,waste:true,noLead:true,from:ci?cs[ci-1].day:null});out.demand.sort(order);
      out.notes.push({day:sc.day,units:C,records:R,waitingUsed:wu,how:"used without a letter: "+fmtInt(R-C)+" more stamps than the letters needed, up to "+fmtDay(sc.day)+by});
    }else if(C>R){
      var price=stampPriceOn(sc.day,"USPS",prices);
      out.lots.push({key:"Postage:found:"+sc.day,exId:null,partIndex:0,comp:"Postage",day:sc.day,bought:sc.day,vendor:"Shelf count",kind:null,units:C-R,cents:(C-R)*price,origUnits:C-R,origCents:(C-R)*price,
        how:"found",why:"your shelf count found "+fmtInt(C-R)+" more stamps than the records (priced at the "+price+"¢ letter rate)",est:true,hand:true});
      out.lots.sort(cmpLot);
      out.notes.push({day:sc.day,units:C,records:R,waitingUsed:wu,how:"found "+fmtInt(C-R)+" stamps the records don't have"+by});
    }else out.notes.push({day:sc.day,units:C,records:R,waitingUsed:wu,how:"matches the records"+by});
  });
  return out;
}

// ── Supply lots: counts for every part ───────────────────────

// The rule Reece gave (trusted — it bypasses the price check): Staples
// résumé / fine-linen envelopes = 50 a box; a ream of copy paper = 500.
function trustedRule(p,settings){
  if(p.type==="Envelopes"&&p.vendor==="Staples"&&/^(Résumé|Premium)/.test(p.variety||"")&&p.qty>0)
    return{units:p.qty*settings.staplesPremiumEnvPerBox,how:"rule",why:"your rule: Staples résumé / fine-linen envelopes = "+settings.staplesPremiumEnvPerBox+" a box"};
  if(p.type==="Paper"&&/^Standard copy/.test(p.variety||"")&&p.qty>0)
    return{units:p.qty*settings.reamSheets,how:"rule",why:"a ream of copy paper = "+fmtInt(settings.reamSheets)+" sheets"};
  return null;
}
// The reference price for a part with no count: the median unit price of
// the counted lots (receipt + rule) — the same kind within ±refDays, else
// the same type within ±refDays, else the same type all time. A HAND row
// with no kind ("Stationary Supplies" $338) and fewer than REF_MIN_LOTS
// counted lots near it takes the type's going price — every counted lot, all
// time, what a shortfall is estimated at — never the median of a handful of
// mixed kinds (review RD-4: two such rows priced like the 3–4 lots bought near
// them — copy paper at 3–4¢ and one parchment — became ~12,900 cheap sheets
// that fed every letter to late August, and September's paper read as
// tripled).
var REF_MIN_LOTS=5;
function refPrice(p,known,settings){
  var near=function(l){return Math.abs(daysBetween(l.bought,p.day))<=settings.refDays;};
  if(p.hand&&!p.variety&&known.length&&known.filter(near).length<REF_MIN_LOTS)return{price:median(known.map(function(l){return l.origCents/l.origUnits;})),n:known.length,scope:"same type, all time — a hand row with no kind"};
  var pools=[[p.variety?known.filter(function(l){return l.kind===p.variety&&near(l);}):[],"same kind ±"+settings.refDays+" days"],[known.filter(near),"same type ±"+settings.refDays+" days"],[known,"same type, all time"]];
  for(var i=0;i<pools.length;i++)if(pools[i][0].length)return{price:median(pools[i][0].map(function(l){return l.origCents/l.origUnits;})),n:pools[i][0].length,scope:pools[i][1]};
  return null;
}
// One part with no count → {units, how, why} (box | price | window).
function inferUnits(part,known,range,settings){
  var ref=refPrice(part,known,settings);
  if(!ref)return{units:null,how:"window",why:"no "+part.type.toLowerCase()+" purchase has a count yet — spread over its letters for 30 days"};
  var sizes=BOX_SIZES[part.type];
  if(part.qty>=1&&sizes&&!part.hand&&range&&isFinite(range.min)){
    var cands=sizes.map(function(b){var units=part.qty*b;return{b:b,units:units,u:part.cents/units};}).filter(function(c){return c.u>=range.min&&c.u<=range.max;});
    if(cands.length){
      cands.sort(function(a,b){return Math.abs(Math.log(a.u/ref.price))-Math.abs(Math.log(b.u/ref.price))||b.b-a.b;});
      var c=cands[0];
      return{units:c.units,how:"box",why:"box size by price: "+part.qty+" × "+c.b+" ("+centsLabel(c.u)+"; counted "+ref.scope+" median "+centsLabel(ref.price)+")"};
    }
  }
  return{units:Math.max(1,Math.round(part.cents/ref.price)),how:"price",why:"priced like "+ref.n+" counted "+(ref.n===1?"lot":"lots")+" ("+ref.scope+", median "+centsLabel(ref.price)+")"};
}
// One type's parts → {lots (FIFO layers with a count, oldest first), all,
// known, range {min, max} (receipt-counted unit cents, all time),
// goingPrice (median counted unit cents, all time), inferred, windows
// (parts with no count at all), adjustments}.
function supplyLots(comp,parts,refundTies,settings){
  settings=settings||normalizeSettings({});
  var ps=(parts||[]).filter(function(p){return p.type===comp&&p.cents;});
  var lots=[];var known=[];var refunds=[];
  ps.forEach(function(p){
    var base={key:p.key,exId:p.exId,partIndex:p.partIndex,comp:comp,day:p.shelf,bought:p.day,vendor:p.vendor,kind:p.variety,brand:p.brand,cents:p.cents,qty:p.qty,hand:p.hand,desc:p.desc,shelfMoved:p.shelf!==p.day};
    if(p.cents<0){refunds.push(Object.assign(base,{units:p.units||0,day:p.day}));return;}
    var u=null;
    if(p.units>0)u={units:p.units,how:"receipt",why:p.fromLine?(p.hand?"the count on its Items line":"count on the receipt"):"the count in its description"};
    else u=trustedRule(p,settings);
    var l=Object.assign(base,{units:u?u.units:null,how:u?u.how:null,why:u?u.why:null});
    lots.push(l);if(u){l.origUnits=l.units;l.origCents=l.cents;known.push(l);}
  });
  var rec=known.filter(function(l){return l.how==="receipt";}).map(function(l){return l.origCents/l.origUnits;});
  var range={min:rec.length?Math.min.apply(null,rec):NaN,max:rec.length?Math.max.apply(null,rec):NaN};
  lots.filter(function(l){return l.units==null;}).forEach(function(l){
    var f=inferUnits({type:comp,day:l.bought,variety:l.kind,qty:l.qty,cents:l.cents,hand:l.hand},known,range,settings);
    l.units=f.units;l.how=f.how;l.why=f.why;
  });
  lots.forEach(function(l){if(l.origUnits==null){l.origUnits=l.units;l.origCents=l.cents;}});
  var goingPrice=median(known.map(function(l){return l.origCents/l.origUnits;}));
  var windows=lots.filter(function(l){return l.how==="window";});
  var counted=lots.filter(function(l){return l.how!=="window";});
  counted.sort(cmpLot);
  var adjustments=netRefunds(counted.concat(windows.map(function(w){return w;})),refunds,refundTies,comp);
  return{lots:counted.filter(function(l){return l.units>0;}),all:lots,known:known,range:range,goingPrice:goingPrice,
    inferred:lots.filter(function(l){return l.how==="box"||l.how==="price";}),windows:windows.filter(function(w){return w.cents>0;}),adjustments:adjustments};
}

// ── Demand ───────────────────────────────────────────────────

function usageOf(settings,comp,day,def){
  var best=null;
  (settings.usage||[]).forEach(function(u){
    if(u.comp!==comp)return;
    if(u.from&&day<u.from)return;if(u.to&&day>u.to)return;
    if(!best||(u.from||"")>(best.from||""))best=u;
  });
  return best?best.perLetter:def;
}
// Per-day units for a component (cumulative rounding, so a fractional usage
// adds up to round(letters × usage) exactly). letters: [{day, printed,
// newCall, envOnly}] (backed, ≤ mailedThrough). → [{day, units, letters}]
function demandFor(comp,letters,settings,opts){
  opts=opts||{};
  var out=[];var acc=0,prev=0;
  (letters||[]).forEach(function(x){
    var n;
    if(comp==="Envelopes")n=x.printed+x.newCall+x.envOnly;
    else n=x.printed+x.newCall;
    if(!n)return;
    var u;
    if(comp==="Envelopes")u=usageOf(settings,comp,x.day,settings.envelopesPerLetter);
    else if(comp==="Paper")u=usageOf(settings,comp,x.day,settings.paperPerLetter);
    else if(comp==="Business Cards"){if(settings.cardsUntil&&x.day>settings.cardsUntil)return;u=usageOf(settings,comp,x.day,settings.cardsPerLetter);}
    else if(comp==="Cardstock"){if(opts.from&&x.day<opts.from)return;u=usageOf(settings,comp,x.day,opts.perLetter!=null?opts.perLetter:0);}
    else u=1;
    acc+=n*u;var r=Math.round(acc+1e-9);var units=r-prev;prev=r;
    if(units>0)out.push({day:x.day,units:units,letters:n});
  });
  return out;
}

// ── Windows ──────────────────────────────────────────────────

// cents spread from `from` to `to` over weightDays [{day, n}] (sorted):
// after mailedThrough → waiting (a window across it is pro-rated by days
// elapsed); a window with no letters → the next letter day after it; none →
// waiting. → {byDay: Map(day → cents), waiting, from, to}
function spreadWindow(c,from,to,weightDays,mailedThrough){
  var out={byDay:new Map(),waiting:0,from:from,to:to};
  if(!c)return out;
  if(from>mailedThrough){out.waiting=c;return out;}
  if(to>mailedThrough){
    var all=daysBetween(from,to)+1,el=daysBetween(from,mailedThrough)+1;
    var now=divRound(c*el,all);out.waiting=c-now;c=now;to=mailedThrough;
  }
  var ds=(weightDays||[]).filter(function(x){return x.n>0&&x.day>=from&&x.day<=to;});
  if(!ds.length){var nx=(weightDays||[]).filter(function(x){return x.n>0&&x.day>=from&&x.day<=mailedThrough;})[0];if(nx)ds=[nx];}
  if(!ds.length){out.waiting+=c;return out;}
  var a=allocate(c,ds.map(function(x){return x.n;}));
  ds.forEach(function(x,i){if(a[i])out.byDay.set(x.day,(out.byDay.get(x.day)||0)+a[i]);});
  return out;
}
// A printer's kind, and an ink / toner purchase's: "laser" (toner) or "ink"
// (an inkjet — MegaTank, EcoTank, a printer that doesn't say); for a purchase
// null when it doesn't say (it goes by its date then).
var LASER_RE=/\b(toner|laser|laserjet|imageclass)\b/i;
var INK_RE=/\b(ink|inks|inkjet|megatank|ecotank|pixma|gi[- ]?\d+|pg[- ]?\d+|cl[- ]?\d+)\b/i;
function printerFamily(text){return LASER_RE.test(text||"")?"laser":"ink";}
function inkFamily(p){var t=(p.desc||"")+" "+(p.variety||"");return/\btoner\b/i.test(t)?"laser":INK_RE.test(t)?"ink":null;}
// Printer eras from Equipment parts that name a printer (purchases ≤
// printerJoinDays apart = one printer: the 4/7 protection plan + the 4/8
// imageCLASS). A printer's era ENDS WHEN THE NEXT PRINTER IS BOUGHT (the day
// before it starts), whatever its kind — Reece, 10/11/26, of the imageCLASS:
// "I stopped using that because of how expensive it was to operate" — or on
// its "retired on" date (any day after it started). Ticked "still in use"
// (`inUse` — off by default) it stays in use past the next printer, until
// its "retired on" date or a new printer of the SAME kind (the review RD-3
// overlap: the laser on the envelopes, the inkjet on the sheets).
// → [{key, ids, parts, from, last, to, cents, retired, retiredOn, replacedBy,
// inUse, family, lifeMonths, desc}]
function printerEras(parts,settings){
  settings=settings||normalizeSettings({});
  var pe=settings.printerEras||{};
  var pp=(parts||[]).filter(function(p){
    if(p.type!=="Equipment"||!p.cents||p.cents<0)return false;
    if(pe[p.exId]&&pe[p.exId].notPrinter)return false;
    var t=oneLine((p.ex&&p.ex.vendor)||"")+" "+p.desc;
    return PRINTER_RE.test(t)&&!NOT_PRINTER_RE.test(p.desc.replace(/protection plan.*$/i,""));
  }).sort(cmpLot);
  var eras=[];
  pp.forEach(function(p){
    var e=eras[eras.length-1];
    if(e&&daysBetween(e.last,p.day)<=settings.printerJoinDays){e.cents+=p.cents;e.last=p.day;if(e.ids.indexOf(p.exId)<0)e.ids.push(p.exId);e.parts.push(p);if(p.desc.length>e.desc.length&&PRINTER_RE.test(p.desc))e.desc=p.desc;}
    else eras.push({key:"era:"+p.exId,ids:[p.exId],parts:[p],from:p.day,last:p.day,cents:p.cents,desc:p.desc});
  });
  eras.forEach(function(e){e.family=printerFamily(e.parts.map(function(p){return oneLine((p.ex&&p.ex.vendor)||"")+" "+p.desc;}).join(" "));});
  eras.forEach(function(e,i){
    var inUse=e.ids.some(function(id){return!!(pe[id]&&pe[id].inUse);});
    var nx=null;for(var j=i+1;j<eras.length;j++)if(!inUse||eras[j].family===e.family){nx=eras[j];break;}
    var ret=null,life=null;e.ids.forEach(function(id){var o=pe[id];if(!o)return;if(o.retiredOn&&o.retiredOn>=e.from&&(!ret||o.retiredOn<ret))ret=o.retiredOn;if(o.lifeMonths)life=o.lifeMonths;});
    var to=ret||(nx?addDays(nx.from,-1):null);
    e.to=to;e.retired=!!to;e.retiredOn=ret;e.replacedBy=!ret&&nx?nx.key:null;e.inUse=inUse;e.lifeMonths=life||settings.printerLifeMonths;
  });
  return eras;
}
// The pieces each printer printed on each counted day (review RD-3): one
// printer in use prints the whole letter — its sheet and its envelope (an
// envelope-only piece: just the envelope); a laser and an inkjet both in use
// split it — the laser the envelopes (Reece's envelope layout is made for the
// imageCLASS), the inkjet the letter sheets. Of two of one kind in use (a
// "retired on" set past its replacement's start), the newer prints.
// → Map(era key → [{day, n}]) (by day, n > 0)
function piecesByPrinter(eras,counted){
  var out=new Map();eras.forEach(function(e){out.set(e.key,[]);});
  counted.forEach(function(x){
    var sheets=x.printed,envs=x.printed+x.envOnly;if(!envs)return;
    var cur=eras.filter(function(e){return e.from<=x.day&&(!e.to||x.day<=e.to);});
    var newest=function(f){var e=null;cur.forEach(function(q){if(q.family===f&&(!e||q.from>=e.from))e=q;});return e;};
    var L=newest("laser"),I=newest("ink");
    var put=function(e,n){if(e&&n>0)out.get(e.key).push({day:x.day,n:n});};
    if(L&&I){put(L,envs);put(I,sheets);}else put(L||I,sheets+envs);
  });
  return out;
}
function eraLabel(e,ink){
  var t=(e.desc+" "+ink.map(function(p){return p.desc+" "+(p.variety||"");}).join(" ")).toLowerCase();
  if(/toner|imageclass|laserjet|\blaser\b/.test(t))return"Laser — toner";
  if(/megatank|\bgi[- ]?\d/.test(t))return"MegaTank — ink";
  if(/ecotank/.test(t))return"EcoTank — ink";
  return"Printer — ink";
}

// ── The whole picture ────────────────────────────────────────

function newDay(day){return{day:day,letters:emptyLetters(),cents:{},est:{},feeds:[]};}
// mktgLeads, expenses → the point-in-time model. opts: {owner, loans, today,
// events, config {settings, letterSources, shelfCounts}, refundTies (Map
// refund id → [purchase ids] — the view builds it with expenseImport's
// refundTargets), replacedIds (Set — rows flagged "Replaced by the imported
// …"), partsOf}.
function buildPointInTime(mktgLeads,expenses,opts){
  opts=opts||{};
  var config=opts.config||{};
  var settings=normalizeSettings(config.settings);
  var today=opts.today||todayLocal();
  var partsOf=opts.partsOf||cachedParts;
  var owner=opts.owner||"reece";
  var replaced=opts.replacedIds instanceof Set?opts.replacedIds:new Set(opts.replacedIds||[]);
  var ties=opts.refundTies instanceof Map?opts.refundTies:new Map();
  // 0. Left out (never in spent).
  var leftOut=[];var mine=(expenses||[]).filter(function(e){return e&&(!opts.owner||(e.owner||"reece")===owner);});
  var nlc=new Set(settings.notLetterCost);
  var live=mine.filter(function(e){
    if(replaced.has(e.id)){leftOut.push({id:e.id,ex:e,reason:"replaced",why:"flagged “Replaced by the imported …” — a hand entry the receipts replace",fix:"delete it if it's the same purchase as a receipt; Keep it in Review only if it's a different one"});return false;}
    if(looksLikeRefund(e,partsOf)){leftOut.push({id:e.id,ex:e,reason:"refund",why:"looks like a refund (its description / items say refund or return) but its amount is positive",fix:"Edit → tick Refund"});return false;}
    if(nlc.has(e.id)){leftOut.push({id:e.id,ex:e,reason:"notLetterCost",why:"you marked it “Not a letter cost”",fix:"untick “Not a letter cost” in its panel"});return false;}
    return true;
  });
  var parts=listParts(live,partsOf,settings);
  // 1. Letters.
  // whose leads' letters: opts.leadOwner (null = every lead — the ownership switch off), else the owner
  var leadOwner=opts.leadOwner!==undefined?opts.leadOwner:owner;
  var ledger=letterLedger(mktgLeads,live,{owner:leadOwner,loans:opts.loans,today:today,events:opts.events,sources:config.letterSources,mailedThrough:settings.mailedThrough,partsOf:partsOf});
  var thru=ledger.mailedThrough;
  var letterDays=Array.from(ledger.days.keys()).sort();
  var days=new Map();
  var D=function(d){var x=days.get(d);if(!x){x=newDay(d);days.set(d,x);}return x;};
  var addC=function(d,k,c,feed){if(!c&&!(feed&&feed.est))return;var x=D(d);x.cents[k]=(x.cents[k]||0)+c;if(feed)x.feeds.push(feed);};
  var addE=function(d,k,c,feed){if(!c)return;var x=D(d);x.est[k]=(x.est[k]||0)+c;if(feed)x.feeds.push(feed);};
  // the waiting letters that were printed in-house (never the mailhouse's), by
  // kind — only what a shelf count takes off the records (§6.3); they take no stock
  var waitingLetters=[];
  letterDays.forEach(function(d){var x=ledger.days.get(d);if(!(x.waiting>0))return;var env=Math.min(x.waiting,x.waitingEnv||0);
    waitingLetters.push({day:d,printed:Math.max(0,x.waiting-(x.waitingMh||0)-env),newCall:0,envOnly:env});});
  // 2. Stamps — the ceiling (daily FIFO, grace days) — and the letter-stamp
  // shelf counts (review RD-1: they used to be saved and ignored).
  var prices=stampPrices({});
  var ST=stampLots(parts,{prices:prices,refundTies:ties});
  var stampDemand=[];
  letterDays.forEach(function(d){if(d>thru)return;var x=ledger.days.get(d);var n=x.printed+x.newCall+x.envOnly;if(n>0)stampDemand.push({day:d,units:n});});
  var SC=stampShelf(ST.lots,stampDemand,waitingLetters,config.shelfCounts,settings,thru,prices);
  var SF=runFifo(SC.lots,SC.demand,{lead:settings.graceDays});
  var backed=new Map();var stampWaste=[];var letterOut=[];
  SF.out.forEach(function(o,i){var dm=SC.demand[i];if(dm.waste)stampWaste.push({o:o,dm:dm});else{backed.set(o.day,o);letterOut.push(o);}});
  // backed letters per method, per day (≤ mailedThrough)
  var counted=[];
  letterDays.forEach(function(d){
    var x=ledger.days.get(d);var y=D(d).letters;
    y.waiting=x.waiting;y.logged=x.logged;
    if(d>thru){return;}
    var o=backed.get(d);var b=o?o.served:0;
    var a=allocate(b,[x.printed,x.newCall,x.envOnly]);
    y.printed=a[0];y.newCall=a[1];y.envOnly=a[2];y.mailhouse=x.mailhouse;y.notBacked=(x.printed+x.newCall+x.envOnly)-b;
    y.hwMailer=Math.min(x.hwMailer,y.printed);
    counted.push({day:d,printed:a[0],newCall:a[1],envOnly:a[2],mailhouse:x.mailhouse});
  });
  var lotsMap=new Map();var windowsMap=new Map();
  var lotInfo=function(l){lotsMap.set(l.key,l);};
  SC.lots.forEach(lotInfo);
  letterOut.forEach(function(o){o.takes.forEach(function(t){
    var lot=lotsMap.get(t.key);var feed={comp:"Postage",src:"lot",key:t.key,units:t.units,cents:t.cents};
    if(lot&&lot.est){feed.est=true;addE(o.day,"Postage",t.cents,feed);}else addC(o.day,"Postage",t.cents,feed);
  });});
  var stampUse=new Map();SF.lots.forEach(function(l){stampUse.set(l.key,l);});
  // 3. Supply lots.
  var printedDays=counted.map(function(x){return{day:x.day,n:x.printed+x.envOnly};}).filter(function(x){return x.n>0;});
  var stampedDays=counted.map(function(x){return{day:x.day,n:x.printed+x.newCall+x.envOnly};}).filter(function(x){return x.n>0;});
  var allDays=counted.map(function(x){return{day:x.day,n:x.printed+x.newCall+x.envOnly+x.mailhouse};}).filter(function(x){return x.n>0;});
  var waitingItems=[];var adjustments=ST.adjustments.slice();
  var addWaiting=function(comp,label,c,key,exId){if(c)waitingItems.push({comp:comp,label:label,cents:c,key:key||null,exId:exId||null});};
  var SUP={};var usage={};var gaps={};var stock={};
  // business cards by time window (the default — step 4b); "fifo" = one a letter while the cards on record last
  var cardWindows=settings.cardsMode!=="fifo";var cardLots=[];
  LOT_COMPS.forEach(function(comp){
    var S=supplyLots(comp,parts,ties,settings);
    var byWindow=cardWindows&&comp==="Business Cards";
    if(!byWindow){S.lots.forEach(lotInfo);S.windows.forEach(lotInfo);}
    adjustments=adjustments.concat(S.adjustments);
    // usage
    var perLetter,mode,source;
    if(comp==="Cardstock"&&settings.cardstock==="fromPurchases"){
      var first=S.lots.length?S.lots.reduce(function(m,l){return!m||l.day<m?l.day:m;},null):null;
      var units=sum(S.lots.filter(function(l){return l.day<=thru;}),function(l){return l.units;});
      var lets=first?sum(counted.filter(function(x){return x.day>=first;}),function(x){return x.printed+x.newCall;}):0;
      perLetter=lets?units/lets:0;mode="fromPurchases";source=first?fmtInt(units)+" sheets bought ÷ "+fmtInt(lets)+" letters since "+fmtDay(first):"no cardstock purchases";
      S.cardstockFrom=first;
    }else{
      perLetter=comp==="Paper"?settings.paperPerLetter:comp==="Envelopes"?settings.envelopesPerLetter:comp==="Business Cards"?settings.cardsPerLetter:Number(settings.cardstock);
      mode="setting";source=comp==="Business Cards"?(settings.cardsUntil?"one a letter through "+fmtDay(settings.cardsUntil):"one a letter"):"default";
      if(byWindow){mode="window";source=CARDS_WINDOW_NOTE+(settings.cardsUntil?" (through "+fmtDay(settings.cardsUntil)+")":"");}
    }
    usage[comp]={mode:mode,perLetter:perLetter,source:source,overrides:(settings.usage||[]).filter(function(u){return u.comp===comp;})};
    var dem=demandFor(comp,counted,settings,{perLetter:perLetter,from:S.cardstockFrom});
    // (by time window: no FIFO, no stock on hand, no gap — each order is spread in step 4b)
    if(byWindow){
      cardLots=S.all.filter(function(l){return l.cents>0;}).sort(cmpLot);
      SUP[comp]={S:S,F:{lots:[],out:[],onHand:{units:0,cents:0}},dem:dem,shelfNotes:[],extra:[],byWindow:true};
      stock[comp]={units:0,cents:0,estCents:0,lots:[]};
      gaps[comp]=[];
      return;
    }
    // shelf counts: a count below the records raises the span's usage; above
    // them adds found stock at the going price (estimated). The records at a
    // count's day first lose what the WAITING letters printed by then used
    // (they never take stock in the model, but they were printed — a count
    // taken today, after mailedThrough, is that much lower); more used than
    // that with no counted letter in the span is used after mailedThrough →
    // it waits with its letters, never charged to a day after mailedThrough.
    var counts=(config.shelfCounts||[]).filter(function(s){return s&&s.comp===comp&&isDay(s.day)&&isFinite(Number(s.units))&&Number(s.units)>=0;}).sort(function(a,b){return a.day<b.day?-1:1;});
    var extra=[];var shelfNotes=[];
    var lotsNow=function(){return S.lots.concat(extra);};
    var waitDem=counts.length?demandFor(comp,waitingLetters,settings,{perLetter:perLetter,from:S.cardstockFrom}):[];
    counts.forEach(function(sc,ci){
      var prevDay=ci?counts[ci-1].day:null;
      var R0=runFifo(lotsNow().filter(function(l){return l.day<=sc.day;}),dem.filter(function(x){return x.day<=sc.day;}),{}).onHand.units;
      var wu=Math.min(R0,sum(waitDem.filter(function(x){return x.day<=sc.day;}),function(x){return x.units;}));
      var R=R0-wu;
      var C=Math.round(Number(sc.units));
      var by=wu?" (after the "+fmtInt(wu)+" "+unitWord(comp,wu)+" the letters printed after "+fmtDay(thru)+" used)":"";
      if(C<R){
        var span=dem.filter(function(x){return x.day<=sc.day&&(!prevDay||x.day>prevDay);});
        var Dn=sum(span,function(x){return x.units;});
        if(Dn>0){var acc2=0,prev2=0,tot=Dn+R-C;span.forEach(function(x){acc2+=x.units*tot/Dn;var r2=Math.round(acc2+1e-9);x.units=r2-prev2;prev2=r2;});}
        else{dem.push({day:sc.day,units:R-C,letters:0,shelf:true});dem.sort(function(a,b){return a.day<b.day?-1:a.day>b.day?1:0;});}
        shelfNotes.push({day:sc.day,units:C,records:R,waitingUsed:wu,how:"used more than one a letter: "+fmtInt(R-C)+" more "+unitWord(comp,R-C)+" used up to "+fmtDay(sc.day)+by});
      }else if(C>R){
        var gp=S.goingPrice||0;var l={key:comp+":found:"+sc.day,exId:null,partIndex:0,comp:comp,day:sc.day,bought:sc.day,vendor:"Shelf count",kind:null,units:C-R,cents:Math.round((C-R)*gp),how:"found",why:"your shelf count found "+fmtInt(C-R)+" more than the records (priced at the going "+centsLabel(gp)+")",est:true,hand:true};
        extra.push(l);lotInfo(l);
        shelfNotes.push({day:sc.day,units:C,records:R,waitingUsed:wu,how:"found "+fmtInt(C-R)+" "+unitWord(comp,C-R)+" the records don't have"+by});
      }else shelfNotes.push({day:sc.day,units:C,records:R,waitingUsed:wu,how:"matches the records"+by});
    });
    var F=runFifo(lotsNow(),dem,{});
    var estimated=comp==="Envelopes"||comp==="Paper";
    var usedAfter=0;
    // the letters a short day's missing units were for (a cardstock sheet isn't a letter — review S9)
    var shortLetters=function(o,i){var d=dem[i];return d&&d.letters&&o.need?Math.round(d.letters*o.short/o.need):0;};
    F.out.forEach(function(o,i){
      // (stock a shelf count says was used after mailedThrough: waiting, never charged)
      if(o.day>thru){o.takes.forEach(function(t){var lot=lotsMap.get(t.key);if(!(lot&&lot.est))usedAfter+=t.cents;});return;}
      o.takes.forEach(function(t){
        var lot=lotsMap.get(t.key);
        if(lot&&lot.est){addE(o.day,comp,t.cents,{comp:comp,src:"lot",key:t.key,units:t.units,cents:t.cents,est:true});}
        else addC(o.day,comp,t.cents,{comp:comp,src:"lot",key:t.key,units:t.units,cents:t.cents});
      });
      if(o.short){
        var ec=estimated&&S.goingPrice?Math.round(o.short*S.goingPrice):0;
        o.estC=ec;o.shortLetters=shortLetters(o,i);
        var x=D(o.day);x.feeds.push({comp:comp,src:"short",key:comp+":short",units:o.short,letters:o.shortLetters,cents:ec,est:true});
        if(ec)x.est[comp]=(x.est[comp]||0)+ec;
      }
    });
    addWaiting(comp,comp.toLowerCase()+" used after "+fmtDay(thru)+" (your shelf count)",usedAfter,comp+":usedAfter",null);
    // window parts (no count anywhere for the type): spread over the type's demand days, 30 days
    S.windows.forEach(function(w){
      var wd=dem.map(function(x){return{day:x.day,n:x.units};});
      var sp=spreadWindow(w.cents,w.bought,addDays(w.bought,29),wd,thru);
      var key="supply:"+w.key;
      windowsMap.set(key,{key:key,comp:comp,kind:"supply",label:w.vendor+" "+comp.toLowerCase()+" (no count)",parts:[{exId:w.exId,partIndex:w.partIndex,cents:w.cents,key:w.key}],from:w.bought,to:addDays(w.bought,29),cents:w.cents,byDay:sp.byDay,waiting:sp.waiting,lot:w});
      sp.byDay.forEach(function(c,d){addC(d,comp,c,{comp:comp,src:"window",key:key,cents:c});});
      addWaiting(comp,"no-count "+comp.toLowerCase()+" window",sp.waiting,key,w.exId);
    });
    SUP[comp]={S:S,F:F,dem:dem,shelfNotes:shelfNotes,extra:extra};
    stock[comp]={units:F.onHand.units,cents:sum(F.lots.filter(function(l){return!l.est;}),function(l){return l.leftC;}),estCents:sum(F.lots.filter(function(l){return l.est;}),function(l){return l.leftC;}),
      lots:F.lots.filter(function(l){return l.left>0;}).map(function(l){return{key:l.key,exId:l.exId,units:l.left,cents:l.leftC,est:!!l.est};})};
    gaps[comp]=runsOf(F.out.filter(function(o){return o.short>0&&o.day<=thru;}).map(function(o){return{day:o.day,units:o.short,est:o.estC||0,letters:o.shortLetters||0};}),comp);
  });
  stock.Postage={units:SF.onHand.units,cents:sum(SF.lots.filter(function(l){return!l.est;}),function(l){return l.leftC;}),estCents:sum(SF.lots.filter(function(l){return l.est;}),function(l){return l.leftC;}),
    lots:SF.lots.filter(function(l){return l.left>0;}).map(function(l){return{key:l.key,exId:l.exId,units:l.left,cents:l.leftC,est:!!l.est};})};
  gaps.Postage=runsOf(letterOut.filter(function(o){return o.short>0;}).map(function(o){return{day:o.day,units:o.short,est:0,letters:o.short};}),"Postage");
  // 4. Printers + ink & toner: each printer's ink / toner — by kind (toner →
  // the laser, GI-xx ink → the inkjet; a purchase that doesn't say goes by its
  // date) — over the pieces IT printed (piecesByPrinter; review RD-3).
  var eras=printerEras(parts,settings);
  var ink=parts.filter(function(p){return p.type==="Ink & Toner";}).map(function(p){return{key:p.key,exId:p.exId,partIndex:p.partIndex,comp:"Ink & Toner",day:p.day,bought:p.day,vendor:p.vendor,kind:p.variety,cents:p.cents,desc:p.desc,variety:p.variety,hand:p.hand};});
  var inkLots=ink.filter(function(p){return p.cents>0;}).map(function(p){return Object.assign({},p);}).sort(cmpLot);   // copies: netting never touches what was spent
  adjustments=adjustments.concat(netRefunds(inkLots,ink.filter(function(p){return p.cents<0;}).map(function(r){return Object.assign({},r,{units:0});}),ties,"Ink & Toner"));
  inkLots=inkLots.filter(function(p){return p.cents>0;});
  var eraOf=function(d){var e=null;eras.forEach(function(x){if(x.from<=d)e=x;});return e||eras[0]||null;};
  var eraFor=function(p){
    var f=inkFamily(p);var fam=f?eras.filter(function(e){return e.family===f;}):[];
    if(!fam.length)return eraOf(p.day);
    var e=null;fam.forEach(function(x){if(x.from<=p.day)e=x;});return e||fam[0];
  };
  var pieces=piecesByPrinter(eras,counted);
  var printerLeft=0;
  eras.forEach(function(e){e.ink=inkLots.filter(function(p){return eraFor(p)===e;});e.label=eraLabel(e,e.ink);e.inkCents=sum(e.ink,function(p){return p.cents;});
    e.pieces=pieces.get(e.key)||[];e.piecesPrinted=sum(e.pieces,function(x){return x.n;});
    e.letters=sum(printedDays.filter(function(x){return x.day>=e.from&&x.day<=(e.to||thru);}),function(x){return x.n;});});
  var noEraInk=eras.length?[]:inkLots.slice();
  // a window part's purchase (what "What was in use" names it by — review S4)
  var partOf=function(p){return{exId:p.exId,partIndex:p.partIndex,cents:p.cents,key:p.key,day:p.day,vendor:p.vendor||null,kind:p.variety||null,desc:p.desc||""};};
  var putWindow=function(key,o,sp){
    o.byDay=sp.byDay;o.waiting=sp.waiting;o.charged=0;sp.byDay.forEach(function(c){o.charged+=c;});
    windowsMap.set(key,o);
    var comp=o.comp;var ks=o.parts.length>1?o.parts:null;
    sp.byDay.forEach(function(c,d){
      if(!ks){addC(d,comp,c,{comp:comp,src:"window",key:key,cents:c,exId:o.parts[0]&&o.parts[0].exId});return;}
      var a=allocate(c,ks.map(function(p){return p.cents;}));
      var x=D(d);x.cents[comp]=(x.cents[comp]||0)+c;
      ks.forEach(function(p,i){if(a[i])x.feeds.push({comp:comp,src:"window",key:key,cents:a[i],exId:p.exId,partKey:p.key});});
    });
    addWaiting(comp,o.waitLabel||o.label,sp.waiting,key,o.parts[0]&&o.parts[0].exId);
  };
  if(settings.inkMode==="era"){
    eras.forEach(function(e){
      var mineInk=e.ink.filter(function(p){return p.day<=thru;});
      var c=sum(mineInk,function(p){return p.cents;});
      if(c){
        var sp=spreadWindow(c,e.from,e.to&&e.to<thru?e.to:thru,e.pieces,thru);
        putWindow("ink:"+e.key,{key:"ink:"+e.key,comp:"Ink & Toner",kind:"inkEra",label:e.label+" ("+fmtDay(e.from)+(e.to?" – "+fmtDay(e.to):" on")+")",parts:mineInk.map(partOf),from:e.from,to:e.to||thru,cents:c,era:e.key},sp);
      }
      e.ink.filter(function(p){return p.day>thru;}).forEach(function(p){addWaiting("Ink & Toner","ink bought after "+fmtDay(thru),p.cents,"ink:"+p.key,p.exId);});
    });
  }else{
    inkLots.forEach(function(p){
      var e=eraFor(p);
      var nx=inkLots.filter(function(q){return q.day>p.day&&eraFor(q)===e;})[0];
      var to=nx?addDays(nx.day,-1):addDays(p.day,29);
      if(e&&e.to&&to>e.to)to=e.to;
      // a retired printer's toner with none of its pieces on or after the day it was bought (bought just
      // before it was retired, or after) is still that printer's — over its whole era, never left over
      // (10/11/26); otherwise a window with no pieces goes on the next one it printed (spreadWindow)
      var from=p.day;
      if(e&&e.to&&p.day<=thru&&!(e.pieces||[]).some(function(x){return x.day>=from;})){from=e.from;to=e.to;}
      var sp=spreadWindow(p.cents,from,to,e?e.pieces:printedDays,thru);
      putWindow("ink:"+p.key,{key:"ink:"+p.key,comp:"Ink & Toner",kind:"ink",label:"ink / toner "+fmtDay(p.day),parts:[partOf(p)],from:from,to:to,cents:p.cents},sp);
    });
  }
  noEraInk.forEach(function(p){if(settings.inkMode==="era"){var sp=spreadWindow(p.cents,p.day,addDays(p.day,29),printedDays,thru);putWindow("ink:"+p.key,{key:"ink:"+p.key,comp:"Ink & Toner",kind:"ink",label:"ink / toner "+fmtDay(p.day),parts:[partOf(p)],from:p.day,to:addDays(p.day,29),cents:p.cents},sp);}});
  eras.forEach(function(e){
    var key="printer:"+e.key;
    var ps=e.parts.map(partOf);
    var o={key:key,comp:"Printers",kind:"printer",label:e.label.replace(/ — .*$/,"")+" printer",parts:ps,from:e.from,to:e.to||null,cents:e.cents,era:e.key,retired:e.retired,lifeMonths:e.lifeMonths};
    if(e.retired){putWindow(key,o,spreadWindow(e.cents,e.from,e.to,e.pieces,thru));return;}
    var life=Math.round(e.lifeMonths*30.4375);
    var per=allocate(e.cents,Array.apply(null,Array(life)).map(function(){return 1;}));
    var pd=new Map();e.pieces.forEach(function(x){pd.set(x.day,x.n);});
    var byDay=new Map();var carry=0;var left=0;var d=e.from;
    for(var k=0;k<life;k++,d=addDays(d,1)){
      if(d>thru){left+=per[k];continue;}
      carry+=per[k];
      if(pd.get(d)){byDay.set(d,(byDay.get(d)||0)+carry);carry=0;}
    }
    left+=carry;printerLeft+=left;
    o.notUsedUp=left;o.lifeDays=life;
    putWindow(key,o,{byDay:byDay,waiting:0});
  });
  // 4b. Business cards by time window (10/11/26 — Reece: "they didn't all get
  // a card"; the default, settings.cardsMode "window"): each order (refunds
  // netted) over the card letters — printed + New Call, the ones he stuffed;
  // never an envelope-only piece or the mailhouse's — from its shelf day to
  // the day before the next LATER order (two the same day share), the last to
  // the cards-through date (none set → mailedThrough). Letters after that
  // date carry none. A window with no letters → the next card letter
  // (spreadWindow); none at all (an order after the cards-through date) →
  // waiting. Never lost: spent = charged + waiting to the cent.
  // A dated change for Business cards (settings.usage — "0 a letter from 4/15
  // to 4/30" = those letters had no card) weighs its days' letters: 0 takes
  // them out of the order's spread, 0.5 gives them half a share (review 10/11:
  // it used to be saved, listed and ignored here). Weights = demandFor's units
  // at one a letter (cumulative rounding); `letters` = the letters themselves.
  var cardEnd=settings.cardsUntil||thru;
  var cardDays=demandFor("Business Cards",counted.filter(function(x){return x.day<=cardEnd;}),Object.assign({},settings,{cardsPerLetter:1}),{})
    .map(function(x){return{day:x.day,n:x.units,letters:x.letters};});
  cardLots.forEach(function(l,i){
    var nx=null;for(var j=i+1;j<cardLots.length;j++)if(cardLots[j].day>l.day){nx=cardLots[j];break;}
    var to=nx?addDays(nx.day,-1):cardEnd;if(to>cardEnd)to=cardEnd;if(to<l.day)to=l.day;
    var key="cards:"+l.key;
    var p={exId:l.exId,partIndex:l.partIndex,cents:l.cents,key:l.key,day:l.bought,vendor:l.vendor||null,kind:l.kind||null,desc:l.desc||""};
    var sp=spreadWindow(l.cents,l.day,to,cardDays,thru);
    // its letters: the card letters in its window (≤ mailedThrough), else the next one it went on (spreadWindow's rule)
    var hi=to>thru?thru:to;var ws=l.day>thru?[]:cardDays.filter(function(x){return x.day>=l.day&&x.day<=hi;});
    if(!ws.length&&l.day<=thru)ws=cardDays.filter(function(x){return x.day>=l.day;}).slice(0,1);
    var lt=sum(ws,function(x){return x.letters;});
    // bought after the cards-through date he set: no letter will carry it unless that date moves (review RV-3)
    var after=!!settings.cardsUntil&&l.day>settings.cardsUntil;
    var label=(l.vendor&&l.vendor!==NO_VENDOR?l.vendor+" ":"")+"business cards "+fmtDay(l.bought);
    putWindow(key,{key:key,comp:"Business Cards",kind:"cards",label:label,parts:[p],from:l.day,to:to,cents:l.cents,units:l.units||null,letters:lt,afterCards:after,cardsUntil:after?settings.cardsUntil:null,
      waitLabel:after?label+" — bought after the cards-through date ("+fmtDay(settings.cardsUntil)+"); no letter carries it":null},sp);
  });
  // 5. Data — each purchase until its site's next (30–60 days), over every counted letter.
  var dataParts=parts.filter(function(p){return p.type==="Data";}).map(function(p){return Object.assign({},p,{site:dataSiteIn(oneLine((p.ex&&p.ex.vendor)||"")+" "+p.desc)||p.vendor});}).sort(cmpLot);
  var dataPos=dataParts.filter(function(p){return p.cents>0;});
  var dataLots=dataPos.map(function(p){return{key:p.key,exId:p.exId,partIndex:p.partIndex,day:p.day,bought:p.day,cents:p.cents,kind:p.site};});
  adjustments=adjustments.concat(netRefunds(dataLots,dataParts.filter(function(p){return p.cents<0;}).map(function(p){return{key:p.key,exId:p.exId,day:p.day,cents:p.cents,kind:p.site,units:0};}),ties,"Data"));
  dataPos.forEach(function(p,i){
    var c=dataLots[i].cents;if(!c)return;
    var nx=dataPos.filter(function(q){return q.site===p.site&&q.day>p.day;})[0];
    var to=nx?addDays(nx.day,-1):addDays(p.day,settings.dataMinDays-1);
    if(to<addDays(p.day,settings.dataMinDays-1))to=addDays(p.day,settings.dataMinDays-1);
    if(to>addDays(p.day,settings.dataMaxDays-1))to=addDays(p.day,settings.dataMaxDays-1);
    putWindow("data:"+p.key,{key:"data:"+p.key,comp:"Data",kind:"data",label:p.site+" "+fmtDay(p.day),parts:[{exId:p.exId,partIndex:p.partIndex,cents:c,key:p.key}],from:p.day,to:to,cents:c,site:p.site},spreadWindow(c,p.day,to,allDays,thru));
  });
  // 6. Add-on stamps (never letters) — purchase → next add-on purchase, ≤ 60 days, over stamped letters.
  var addOns=ST.addOns.filter(function(a){return a.cents>0;}).sort(cmpLot);
  ST.addOns.filter(function(a){return a.cents<0;}).forEach(function(r){adjustments.push({comp:"Postage",day:r.day,cents:r.cents,key:r.key,exId:r.exId,why:"add-on stamps refunded"});});
  addOns.forEach(function(p){
    var nx=addOns.filter(function(q){return q.day>p.day;})[0];var cap=addDays(p.day,settings.addOnMaxDays-1);   // the next LATER add-on purchase (a same-day one isn't "next")
    var to=nx&&nx.day>p.day?addDays(nx.day,-1):cap;if(to>cap)to=cap;if(to<p.day)to=p.day;
    putWindow("addon:"+p.key,{key:"addon:"+p.key,comp:"Postage",kind:"addon",label:"Add-on stamps "+fmtDay(p.day),parts:[{exId:p.exId,partIndex:p.partIndex,cents:p.cents,key:p.key}],from:p.day,to:to,cents:p.cents},spreadWindow(p.cents,p.day,to,stampedDays,thru));
  });
  // 7. The mailhouse — its invoices where its letters went out.
  var mhParts=parts.filter(function(p){return p.type==="Postage"&&p.mailhouse;});
  var mhRecs=mhParts.map(function(p){return{day:p.day,comp:"Postage",cents:p.cents,id:p.exId,key:p.key,partIndex:p.partIndex,vendor:p.vendor,mailhouse:true,family:mailhouseFamilyOf(p)};});
  var adapter=new Map();counted.forEach(function(x){if(x.mailhouse)adapter.set(x.day,{total:x.printed+x.newCall+x.envOnly+x.mailhouse,byMethod:{Mailhouse:x.mailhouse}});});
  var MH=mailhousePlacement(mhRecs,adapter);
  // …and the drops whose own mailhouse letters are still waiting (after
  // mailedThrough; the same nearest-group rule) — those invoices wait with them.
  var waitAdapter=new Map();ledger.days.forEach(function(x,d){if(d>thru&&x.waitingMh>0)waitAdapter.set(d,{total:x.waitingMh,byMethod:{Mailhouse:x.waitingMh}});});
  var MHW=waitAdapter.size?mailhousePlacement(mhRecs,waitAdapter):null;
  var mhCents=sum(mhRecs,function(r){return r.cents;});
  MH.groups.forEach(function(g,gi){
    var key="mailhouse:"+gi;
    var gp=mhRecs.filter(function(r){return g.ids.indexOf(r.id)>=0;});
    var ps=gp.map(function(r){return{exId:r.id,partIndex:r.partIndex,cents:r.cents,key:r.key,day:r.day,vendor:r.vendor||null,kind:null,desc:""};});
    var byDay=new Map();var waiting=0;
    var lettersWait=!g.placed&&MHW&&MHW.groups[gi]&&MHW.groups[gi].placed;
    // its OWN placement (never the combined byDay — overlapping groups would take each other's days)
    if(g.placed){g.byDay.forEach(function(c,d){byDay.set(d,(byDay.get(d)||0)+c);});}
    else if(lettersWait)waiting=g.cents;
    else gp.forEach(function(r){var lt=ledger.days.get(r.day);var has=r.day<=thru&&lt&&(lt.printed+lt.newCall+lt.envOnly+lt.mailhouse)>0;if(has)byDay.set(r.day,(byDay.get(r.day)||0)+r.cents);else waiting+=r.cents;});
    putWindow(key,{key:key,comp:"Postage",kind:"mailhouse",label:"Mailhouse ("+(gp[0]?(gp[0].vendor||g.family):g.family)+")",parts:ps,from:g.letterFrom||g.from,to:g.letterTo||g.to,cents:g.cents,letters:g.letters,charges:{from:g.from,to:g.to}},{byDay:byDay,waiting:waiting});
  });
  // 7b. Letter stamps a shelf count says were used without a letter (misprints,
  // pulled duplicates — RD-1): their cost goes on the stamped letters since the
  // count before (never a letter more); none counted then → on the count's day,
  // or waiting when that's after mailedThrough. Found stamps' (estimated) share
  // stays an estimate.
  stampWaste.forEach(function(z){
    var o=z.o,dm=z.dm;var real=[],estC=0;
    o.takes.forEach(function(t){var lot=lotsMap.get(t.key)||{};if(lot.est)estC+=t.cents;else real.push({exId:lot.exId||null,partIndex:lot.partIndex||0,cents:t.cents,key:t.key,day:lot.bought||null,vendor:lot.vendor||null,kind:lot.kind||null,desc:lot.desc||""});});
    var ws=stampedDays.filter(function(x){return(!dm.from||x.day>dm.from)&&x.day<=dm.day;});
    if(estC&&dm.day<=thru)addE(ws.length?ws[ws.length-1].day:dm.day,"Postage",estC,{comp:"Postage",src:"lot",key:"Postage:found-used:"+dm.day,units:0,cents:estC,est:true});
    var c=sum(real,function(p){return p.cents;});if(!c)return;
    var byDay=new Map(),waiting=0;
    if(ws.length){var a=allocate(c,ws.map(function(x){return x.n;}));ws.forEach(function(x,i){if(a[i])byDay.set(x.day,a[i]);});}
    else if(dm.day<=thru)byDay.set(dm.day,c);else waiting=c;
    var key="stampwaste:"+dm.day;
    putWindow(key,{key:key,comp:"Postage",kind:"stampWaste",label:fmtInt(o.served)+" stamps used without a letter (your shelf count on "+fmtDay(dm.day)+")",parts:real,from:ws.length?ws[0].day:dm.day,to:dm.day,cents:c},{byDay:byDay,waiting:waiting});
  });
  // 8. Adjustments on their days.
  adjustments.forEach(function(a){if(!a.cents)return;if(a.day>thru){addWaiting(a.comp,"refund after "+fmtDay(thru),a.cents,a.key,a.exId);a.waiting=true;return;}addC(a.day,a.comp==="Mailhouse"?"Postage":a.comp,a.cents,{comp:a.comp,src:"adj",key:"adj:"+a.key,cents:a.cents,exId:a.exId,why:a.why});});
  // 9. Day records → letters on every day (also days with only charges).
  ledger.days.forEach(function(x,d){D(d);});
  // 10. Spent + reconcile.
  var spentBy={Postage:sum(parts.filter(function(p){return p.type==="Postage"&&!p.mailhouse&&p.variety!==POSTAGE_KINDS.MAILHOUSE&&!isParcelPart(p,ST);}),function(p){return p.cents;})+mhCents};
  LOT_COMPS.forEach(function(c){spentBy[c]=sum(parts.filter(function(p){return p.type===c;}),function(p){return p.cents;});});
  spentBy["Ink & Toner"]=sum(ink,function(p){return p.cents;});
  spentBy.Printers=sum(eras,function(e){return e.cents;});
  spentBy.Data=sum(dataParts,function(p){return p.cents;});
  var res={basis:"pointInTime",keys:PIT_KEYS.slice(),today:today,mailedThrough:thru,settings:settings,ledger:ledger,days:days,lots:lotsMap,windows:windowsMap,eras:eras,
    stamps:{lots:ST.lots,uses:stampUse,bought:sum(ST.lots,function(l){return l.units;}),boughtCents:sum(ST.lots,function(l){return l.cents;}),needing:sum(stampDemand,function(d){return d.units;}),backed:sum(letterOut,function(o){return o.served;}),notBacked:sum(letterOut,function(o){return o.short;}),
      onHand:{units:SF.onHand.units,cents:stock.Postage.cents,estCents:stock.Postage.estCents},shelfNotes:SC.notes,usedWithoutLetter:sum(stampWaste,function(z){return z.o.served;}),addOns:ST.addOns,addOnCents:sum(ST.addOns,function(a){return a.cents;}),parcels:ST.parcels,parcelCents:sum(ST.parcels,function(a){return a.cents;}),prices:prices,mailhouse:{cents:mhCents,groups:MH.groups},grace:settings.graceDays},
    supplies:SUP,stock:stock,waiting:{items:waitingItems,cents:sum(waitingItems,function(w){return w.cents;})},printerLeft:printerLeft,adjustments:adjustments,gaps:gaps,usage:usage,
    leftOut:leftOut.concat(ST.parcels.map(function(p){return{id:p.exId,ex:p.ex||null,reason:"parcel",cents:p.cents,why:"parcel / shipping postage — not a letter cost",fix:"none needed"};})),
    spentBy:spentBy,owner:owner,parts:parts,untyped:untypedRows(live,partsOf)};
  res.notLetterPart=notLetterPartRows(live,partsOf,eras,new Set(res.untyped.map(function(u){return u.id;})));
  res.duplicates=possibleDuplicates(live,partsOf);
  res.reconcile=reconcile(res);
  res.toConfirm=countsToConfirm(res);
  res.sources=sourcesSummary(res,config.letterSources);
  res.compareDefaults=pitCompareDefaults(res);
  markUsedLate(res);
  return res;
}
// Which mailhouse a part is from (costPerLetter's mailhouseFamily): its vendor's first word, else the mailhouse words in its description.
var MH_WORDS_RE=/alpha ?graphics|mail ?house|mailing service|letter ?shop|presort|\beddm\b|every door direct|mail ?shop/i;
function mailhouseFamilyOf(p){
  if(p.vendor&&p.vendor!==NO_VENDOR){var w=oneLine(p.vendor).toLowerCase().split(" ")[0].replace(/[^a-z0-9]/g,"");if(w)return w;}
  var m=MH_WORDS_RE.exec(p.desc||"");return m?m[0].toLowerCase().replace(/[^a-z]/g,""):"";
}
function isParcelPart(p,ST){return ST.parcels.some(function(x){return x.key===p.key;});}
// Runs of short days (merged when ≤ 7 days apart) → [{from, to, letters, units, estCents}]
function runsOf(list,comp){
  var runs=[];
  list.forEach(function(o){var r=runs[runs.length-1];if(r&&daysBetween(r.to,o.day)<=7){r.to=o.day;r.units+=o.units;r.letters+=o.letters;r.estCents+=o.est;}else runs.push({comp:comp,from:o.day,to:o.day,units:o.units,letters:o.letters,estCents:o.est});});
  return runs;
}
function gapsOf(pit){return pit.gaps;}
// What's spent = charged + on hand + waiting + printer not yet used up +
// adjustments (to the cent); estimates (no receipt yet) are separate.
function reconcile(pit){
  var charged=0,est=0,adj=0;
  pit.days.forEach(function(x){
    x.feeds.forEach(function(f){if(f.est){est+=f.cents;return;}if(f.src==="adj")adj+=f.cents;else charged+=f.cents;});
  });
  var onHand=pit.stock.Postage.cents;LOT_COMPS.forEach(function(c){onHand+=pit.stock[c].cents;});
  var spent=0;Object.keys(pit.spentBy).forEach(function(k){spent+=pit.spentBy[k];});
  var waiting=pit.waiting.cents;
  return{spent:spent,charged:charged,onHand:onHand,waiting:waiting,printerLeft:pit.printerLeft,adjustments:adj,estimated:est,diff:spent-charged-onHand-waiting-pit.printerLeft-adj,spentBy:pit.spentBy};
}
// The parts whose counts were inferred (box / price / window — largest $
// first), then Reece's rule lots, then the positive rows that look like refunds.
function countsToConfirm(pit){
  var out=[];
  pit.lots.forEach(function(l){if(l.exId&&(l.how==="box"||l.how==="price"||l.how==="window"))out.push({key:l.key,exId:l.exId,partIndex:l.partIndex,comp:l.comp,how:l.how,why:l.why,units:l.origUnits||l.units,cents:l.origCents||l.cents,day:l.bought,vendor:l.vendor,kind:l.kind,hand:l.hand,group:1});});
  out.sort(function(a,b){return Math.abs(b.cents)-Math.abs(a.cents);});
  var rules=[];pit.lots.forEach(function(l){if(l.exId&&l.how==="rule")rules.push({key:l.key,exId:l.exId,partIndex:l.partIndex,comp:l.comp,how:l.how,why:l.why,units:l.origUnits,cents:l.origCents,day:l.bought,vendor:l.vendor,kind:l.kind,hand:l.hand,group:2});});
  rules.sort(function(a,b){return Math.abs(b.cents)-Math.abs(a.cents);});
  var refunds=pit.leftOut.filter(function(x){return x.reason==="refund";}).map(function(x){return{key:x.id,exId:x.id,partIndex:null,comp:null,how:"refund",why:x.why,fix:x.fix,cents:cents(x.ex&&x.ex.amount),day:localDay(x.ex&&x.ex.date),vendor:x.ex?x.ex.vendor:"",group:3};});
  return out.concat(rules).concat(refunds);
}
// A count-inferred lot still being used more than usedLateDays after the
// next purchase of its type: "used late — its count may be too high".
function markUsedLate(pit){
  LOT_COMPS.forEach(function(comp){
    var F=pit.supplies[comp]&&pit.supplies[comp].F;if(!F)return;
    var all=F.lots;
    all.forEach(function(l){
      var lot=pit.lots.get(l.key);if(!lot||!(lot.how==="box"||lot.how==="price"||lot.how==="window")||!l.used.length)return;
      var nx=all.filter(function(q){return q.day>l.day;})[0];
      var last=l.used[l.used.length-1].day;
      if(nx&&daysBetween(nx.day,last)>pit.settings.usedLateDays)lot.usedLate={last:last,next:nx.day};
    });
  });
}
function sourcesSummary(pit,sources){
  var rows=Array.from(pit.ledger.byMonth.values()).sort(function(a,b){return a.month<b.month?-1:1;});
  return{imports:(sources||[]).map(function(s){return{id:s.id,importedAt:s.importedAt,asOf:s.asOf,window:s.window,files:s.files,totals:s.totals,leftOut:s.leftOut,summaryCheck:s.summaryCheck};}),byMonth:rows,windows:pit.ledger.windows};
}

// ── Periods, per letter, compare ─────────────────────────────

function keysFor(opts){return PIT_KEYS.filter(function(k){return!(k==="Cardstock"&&opts&&opts.cardstockInPaper);});}
function keyFor(k,opts){return k==="Cardstock"&&opts&&opts.cardstockInPaper?"Paper":k;}
// Every day record in a range summed → {letters (counted), byMethod
// {printed, newCall, mailhouse, envOnly}, notBacked, waiting, logged,
// cents{k}, est{k}, shown{k} (+ estimates when included), cost
// (costPerLetter: per letter + components that add up)}.
function pitRange(pit,range,opts){
  opts=Object.assign({includeEstimates:true},opts||{});
  var keys=keysFor(opts);
  var o={from:range&&range.from,to:range&&range.to,letters:0,byMethod:{printed:0,newCall:0,mailhouse:0,envOnly:0,hwMailer:0},notBacked:0,waiting:0,logged:0,cents:{},est:{},shown:{}};
  keys.forEach(function(k){o.cents[k]=0;o.est[k]=0;});
  pit.days.forEach(function(x,d){
    if(range&&((range.from&&d<range.from)||(range.to&&d>range.to)))return;
    var L=x.letters;
    o.byMethod.printed+=L.printed;o.byMethod.newCall+=L.newCall;o.byMethod.mailhouse+=L.mailhouse;o.byMethod.envOnly+=L.envOnly;o.byMethod.hwMailer+=L.hwMailer||0;
    o.letters+=L.printed+L.newCall+L.mailhouse+L.envOnly;o.notBacked+=L.notBacked;o.waiting+=L.waiting;o.logged+=L.logged;
    Object.keys(x.cents).forEach(function(k){o.cents[keyFor(k,opts)]+=x.cents[k];});
    Object.keys(x.est).forEach(function(k){o.est[keyFor(k,opts)]+=x.est[k];});
  });
  keys.forEach(function(k){o.shown[k]=o.cents[k]+(opts.includeEstimates?o.est[k]:0);});
  o.cost=costPerLetter(o.shown,o.letters);
  o.spentTotal=keys.reduce(function(s,k){return s+o.cents[k];},0);
  o.estTotal=keys.reduce(function(s,k){return s+o.est[k];},0);
  return o;
}
// The span the model covers: the first day with a letter or a charge … the
// later of today and the last such day.
function pitSpan(pit){
  var ds=Array.from(pit.days.keys()).filter(function(d){var x=pit.days.get(d);var L=x.letters;return L.printed+L.newCall+L.mailhouse+L.envOnly+L.waiting+L.notBacked>0||Object.keys(x.cents).some(function(k){return x.cents[k];})||Object.keys(x.est).some(function(k){return x.est[k];});}).sort();
  if(!ds.length)return null;
  var to=ds[ds.length-1];if(pit.today>to)to=pit.today;
  return{from:ds[0],to:to};
}
// Periods of a kind ("day" | "week" | "month" | "quarter" | "custom") over a
// range (default: the span), each a sum of day records.
function pitPeriods(pit,kind,range,opts){
  var span=range&&isDay(range.from)&&isDay(range.to)?range:pitSpan(pit);
  if(!span)return[];
  var ps;
  if(kind==="custom")ps=[{key:span.from+"…"+span.to,label:fmtDay(span.from)+" – "+fmtDay(span.to),short:fmtDay(span.from),from:span.from,to:span.to}];
  else if(kind==="week"){
    ps=[];var w=weekKey(span.from);
    while(w<=span.to&&ps.length<600){var end=addDays(w,6);ps.push({key:"W"+w,label:"Week of "+fmtDay(w),short:fmtDay(w).replace(/, \d{4}$/,""),from:w<span.from?span.from:w,to:end>span.to?span.to:end,week:w});w=addDays(w,7);}
  }else ps=periodsBetween(span.from,span.to,kind==="quarter"?"quarter":kind==="day"?"day":"month");
  // a day's axis label without its year ("Sep 10") — the year made them overlap (review S15)
  if(kind==="day")ps=ps.map(function(p){return Object.assign({},p,{short:String(p.short).replace(/, \d{4}$/,"")});});
  return ps.map(function(p){var r=pitRange(pit,p,opts);return Object.assign({},p,r,{afterThru:p.from>pit.mailedThrough,partThru:p.from<=pit.mailedThrough&&p.to>pit.mailedThrough});});
}
// Two ranges side by side → {a, b, rows:[{key, label, a, b, diff, pct, text}], total}
function pitCompare(a,b,pit,opts){
  var keys=keysFor(opts);
  var A=pitRange(pit,a,opts),B=pitRange(pit,b,opts);
  var rows=keys.map(function(k){
    var d=diffInfo({cents:A.shown[k],letters:A.letters},{cents:B.shown[k],letters:B.letters},A.cost.comps[k].perLetter,B.cost.comps[k].perLetter);
    return{key:k,label:PIT_LABEL[k],a:A.cost.comps[k].perLetter,b:B.cost.comps[k].perLetter,aCents:A.shown[k],bCents:B.shown[k],diff:d.diff,pct:d.pct,text:d.text};
  });
  var t=diffInfo({cents:A.cost.cents,letters:A.letters},{cents:B.cost.cents,letters:B.letters});
  return{a:A,b:B,rows:rows,total:{key:"Total",label:"Total",a:A.cost.perLetter,b:B.cost.perLetter,diff:t.diff,pct:t.pct,text:t.text}};
}
// Defaults: the first three months with ≥ 10 counted letters vs the last
// three COMPLETE ones (month end ≤ mailedThrough); fewer than 6 → halves.
// Plus the "Toner era vs Ink era" quick range.
function pitCompareDefaults(pit){
  var by=new Map();
  pit.days.forEach(function(x,d){var L=x.letters;var n=L.printed+L.newCall+L.mailhouse+L.envOnly;if(n)by.set(d.slice(0,7),(by.get(d.slice(0,7))||0)+n);});
  var ms=Array.from(by.keys()).filter(function(k){return by.get(k)>=10;}).sort();
  var complete=ms.filter(function(k){return monthEnd(k)<=pit.mailedThrough;});
  var out=null;
  if(complete.length){
    var n=complete.length;var k=n>=6?3:Math.max(1,Math.floor(n/2));
    out=n===1?{a:{from:complete[0]+"-01",to:monthEnd(complete[0])},b:{from:complete[0]+"-01",to:monthEnd(complete[0])},months:complete}
      :{a:{from:complete[0]+"-01",to:monthEnd(complete[k-1])},b:{from:complete[n-k]+"-01",to:monthEnd(complete[n-1])},months:complete};
  }
  // (printers can overlap — RD-3 — so "before" ends the day before the later one starts)
  var eras=pit.eras||[];
  if(out&&eras.length>=2){
    var e0=eras[0],e1=eras[eras.length-1];var aTo=e0.to||pit.mailedThrough;var cut=addDays(e1.from,-1);if(cut<aTo)aTo=cut;
    if(aTo>=e0.from)out.eras={a:{from:e0.from,to:aTo},b:{from:e1.from,to:e1.to||pit.mailedThrough},aLabel:e0.label,bLabel:e1.label};
  }
  return out;
}

// ── Drill-downs ──────────────────────────────────────────────

// The purchases that fed a component in a range → rows that add up to the
// component's cents there (estimates included when asked): lot takes, window
// shares, gap rows (estimated / no purchase on record), adjustments.
function lotsInPeriod(pit,comp,range,opts){
  opts=Object.assign({includeEstimates:true},opts||{});
  var comps=comp==="Paper"&&opts.cardstockInPaper?["Paper","Cardstock"]:[comp];
  var by=new Map();var shorts=[];
  pit.days.forEach(function(x,d){
    if(range&&((range.from&&d<range.from)||(range.to&&d>range.to)))return;
    x.feeds.forEach(function(f){
      if(comps.indexOf(f.comp)<0&&!(comp==="Postage"&&f.comp==="Mailhouse"))return;
      if(f.src==="short"){shorts.push({day:d,units:f.units,letters:f.letters!=null?f.letters:f.units,cents:f.cents,comp:f.comp});return;}
      if(f.est&&!opts.includeEstimates)return;
      var k=f.src==="window"&&f.partKey?f.key+"|"+f.partKey:f.key;
      var r=by.get(k);
      if(!r){r={src:f.src,key:f.key,partKey:f.partKey||null,comp:f.comp,unitsUsed:0,cents:0,est:!!f.est,first:d,last:d,days:0,exId:f.exId||null,why:f.why||null};by.set(k,r);}
      r.unitsUsed+=f.units||0;r.cents+=f.cents;r.days++;if(d<r.first)r.first=d;if(d>r.last)r.last=d;
    });
  });
  var rows=[];
  by.forEach(function(r){
    if(r.src==="lot"){
      var l=pit.lots.get(r.key)||{};
      rows.push({src:"lot",key:r.key,exId:l.exId||null,partIndex:l.partIndex,comp:r.comp,day:l.bought,vendor:l.vendor,kind:l.kind,brand:l.brand||null,how:l.how,why:l.why,hand:!!l.hand,
        unitsUsed:r.unitsUsed,lotUnits:l.origUnits||l.units,unitCents:l.origUnits?(l.origCents/l.origUnits):l.units?l.cents/l.units:null,cents:r.cents,est:!!l.est||r.est,from:r.first,to:r.last,usedLate:l.usedLate||null,shelfMoved:!!l.shelfMoved});
    }else if(r.src==="window"){
      var w=pit.windows.get(r.key)||{};
      var part=r.partKey?(w.parts||[]).filter(function(p){return p.key===r.partKey;})[0]:(w.parts||[])[0];
      var ex=part?part.exId:r.exId;
      var lt=w.letters!=null?w.letters:null;
      // a window of several purchases (a printer's ink, a mailhouse's invoices): each row names its own (review S4)
      var own=r.partKey&&part?{partDay:part.day||null,partVendor:part.vendor&&part.vendor!==NO_VENDOR?part.vendor:null,partWhat:part.kind||whatOf(part.desc,part.vendor)}:{};
      rows.push(Object.assign({src:"window",key:r.key,partKey:r.partKey,exId:ex||null,partIndex:part?part.partIndex:null,comp:r.comp,kind:w.kind,label:w.label,day:part?part.day||(pit.lots.get(part.key)||{}).bought||w.from:w.from,from:w.from,to:w.to,cents:r.cents,windowCents:w.cents,charged:w.charged,waiting:w.waiting,
        sharePct:w.charged?Math.round(100*r.cents/(r.partKey&&part?divRound(w.charged*part.cents,w.cents):w.charged)):null,letters:lt,vendor:w.site||w.label,est:false},own));
    }else{
      rows.push({src:"adj",key:r.key,exId:r.exId,comp:r.comp,cents:r.cents,from:r.first,to:r.last,why:r.why||"refund adjustment",est:false});
    }
  });
  rows.sort(function(a,b){return(a.src==="lot"?0:a.src==="window"?1:2)-(b.src==="lot"?0:b.src==="window"?1:2)||String(a.day||a.from||"").localeCompare(String(b.day||b.from||""))||String(a.key).localeCompare(String(b.key));});
  // gap rows: runs of short days inside the range
  var runs=runsOf(shorts.sort(function(a,b){return a.day<b.day?-1:1;}).map(function(s){return{day:s.day,units:s.units,est:s.cents,letters:s.letters};}),comp);
  runs.forEach(function(g){rows.push({src:"short",key:comp+":short:"+g.from,comp:comp,from:g.from,to:g.to,units:g.units,letters:g.letters,cents:opts.includeEstimates?g.estCents:0,estCents:g.estCents,est:true,goingPrice:pit.supplies[comp]?pit.supplies[comp].S.goingPrice:null});});
  return rows;
}
// One purchase → how it feeds the cost per letter: [{comp, key, partIndex,
// how, why, units, unitCents, firstUsed, lastUsed, lettersUsed, cents
// (charged), onHand {units, cents}, window {from, to, chargedCents,
// waitingCents, open}, usedLate, duplicateOf, est}]
function lotSummaryFor(pit,expenseId){
  if(!pit||!expenseId)return[];
  var out=[];
  pit.lots.forEach(function(l){
    if(l.exId!==expenseId)return;
    if(l.how==="window"){return;}
    var used=null;
    if(l.comp==="Postage")used=pit.stamps.uses.get(l.key);
    else{var F=pit.supplies[l.comp]&&pit.supplies[l.comp].F;used=F?F.lots.filter(function(q){return q.key===l.key;})[0]:null;}
    var u=used?used.used.filter(function(x){return!x.waste;}):[];   // (stamps a shelf count says went without a letter: their own window line)
    out.push({comp:l.comp,key:l.key,partIndex:l.partIndex,how:l.how,why:l.why,units:l.origUnits||l.units,unitCents:(l.origUnits||l.units)?(l.origCents||l.cents)/(l.origUnits||l.units):null,
      firstUsed:u.length?u[0].day:null,lastUsed:u.length?u[u.length-1].day:null,lettersUsed:sum(u,function(x){return x.units;}),cents:sum(u,function(x){return x.cents;}),
      onHand:used?{units:used.left,cents:used.leftC}:{units:l.units,cents:l.cents},usedLate:l.usedLate||null,est:!!l.est,refunded:l.refunded||0,hand:!!l.hand,shelf:l.day,bought:l.bought});
  });
  pit.windows.forEach(function(w){
    var ps=(w.parts||[]).filter(function(p){return p.exId===expenseId;});
    if(!ps.length)return;
    ps.forEach(function(p){
      var share=w.cents?function(c){return divRound(c*p.cents,w.cents);}:function(){return 0;};
      var charged=ps.length===w.parts.length&&w.parts.length===1?w.charged:share(w.charged||0);
      var waiting=ps.length===w.parts.length&&w.parts.length===1?w.waiting:share(w.waiting||0);
      var lot=w.lot||null;
      out.push({comp:w.comp,key:w.key,partIndex:p.partIndex,how:lot?"window":w.kind,why:lot?lot.why:w.label,window:{from:w.from,to:w.to,chargedCents:charged,waitingCents:waiting,open:!!(w.to&&w.to>pit.mailedThrough)||(w.kind==="printer"&&!w.retired),kind:w.kind,label:w.label,
        daysCharged:w.to&&w.from?Math.min(daysBetween(w.from,w.to),daysBetween(w.from,pit.mailedThrough))+1:null,daysAll:w.to&&w.from?daysBetween(w.from,w.to)+1:null,notUsedUp:w.notUsedUp?share(w.notUsedUp):0,lifeMonths:w.lifeMonths||null,letters:w.letters||null,afterCards:!!w.afterCards,cardsUntil:w.cardsUntil||null},cents:charged});
    });
  });
  var dup=pit.duplicates&&pit.duplicates.get(expenseId);
  if(dup&&dup.length)out.forEach(function(o){o.duplicateOf=dup;});
  if(!out.length&&dup&&dup.length)out.push({comp:null,key:null,duplicateOf:dup});
  return out;
}

export {
  PIT_KEYS, PIT_LABEL, LOT_COMPS, UNIT_OF, DEFAULTS, BOX_SIZES, PRINTER_RE, NOT_PRINTER_RE, CARDS_WINDOW_NOTE,
  normalizeSettings, letterLedger, looksLikeRefund, possibleDuplicates, listParts, netRefunds,
  stampLots, stampShelf, supplyLots, inferUnits, runFifo, demandFor, printerEras, printerFamily, inkFamily, piecesByPrinter, spreadWindow, untypedRows, notLetterPartRows, NEAR_GAP_DAYS,
  buildPointInTime, pitPeriods, pitRange, pitSpan, pitCompare, pitCompareDefaults,
  lotsInPeriod, lotSummaryFor, gapsOf, countsToConfirm, reconcile, weekKey, centsLabel
};
